'use client';

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, CheckCircle, Check, Lock, X, AlertCircle, Info } from 'lucide-react';
import AvatarCircle from '@/components/AvatarCircle';
import { useLeadActivity } from '@/components/forms/useLeadActivity';
import { sfx } from './gm-sound';
import { ButtonCta } from '@/components/ui/ButtonCta';
import { CustomSlider } from '@/components/tools/mortgage-calculator/v3/components/CustomSlider';
import { defaultFieldConfigs } from '@/components/tools/mortgage-calculator/lib/calculator.config';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import confetti from 'canvas-confetti';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
// Lead scoring disabled - needs rebuild. See lead-scoring.ts for old logic
// import { calculateLeadScore, type LeadScore } from '@/lib/lead-scoring';
import { validateEmail as validateEmailShared, validatePhone as validatePhoneShared } from '@/lib/contact-validation';
import { validateNMLS, generateGreeting, extractNames, extractCompany } from '@/lib/modelMatchAPI';
import type { NMLSValidationState, EnrichedData, ValidateNMLSResponse, ConfirmNMLSRequest, ConfirmNMLSResponse } from '@/types/modelMatch';
import './gm.css';
import { GMGame, GMJourney, GMBoss, GMSplit, GMUnlock, CrmMark, type GMState } from './gm-kit';
import { DemoBaseContext } from './demo-base';

interface MortgageMarketingDemoFormProps {
  // No props needed for standalone page version
}

interface CalculatorSnapshot {
  modified?: boolean;
  // Shown to user
  adSpend: number;
  commission: number;
  contactRate: number;
  // Captured silently
  costPerClick?: number;
  landingPageConversionRate?: number;
  applicationRate?: number;
  closeRate?: number;
  purchasePercent?: number;
  refinancePercent?: number;
  agentReferralRatio?: number;
  clientReferralRatio?: number;
  databaseConversionRate?: number;
  organicPerSpend?: number;
  monthlyLeads?: number;
  monthlyLoans?: number;
  costPerLead?: number;
  costPerLoan?: number;
  monthlyROI?: number;
  strategyType?: 'paid_ads' | 'hybrid';
  viewMode?: 'snapshot' | 'timeline';
}

interface FormData {
  // Calculator context
  calculatorConfirmed: boolean;
  calculatorValues?: CalculatorSnapshot;

  // Step responses
  nmls: string;
  role: string;
  currentVolume: string;
  teamSize: string;
  yearsInIndustry: string;
  marketingBudget: string;
  avgLoanSize: string;
  leadGenExperience: string;
  currentLeadSources: string[];
  agencyExperience: string;
  crmSystem: string;
  websiteStatus: string;
  websitePerformance: string;
  websiteUrl: string;
  agentPartnerships: string;
  agentPartnershipImportance: string;
  biggestChallenge: string;
  leadGenApproach: string;
  systemPreference: string;
  timeline: string;
  primaryState: string;
  additionalStates: string[];

  // Contact info
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  bestTimeToCall: string;

  // Metadata
  startedAt: string;
  completedAt?: string;
  timePerStep: Record<number, number>;
  // leadScore?: LeadScore; // Lead scoring disabled - needs rebuild
}

// Step configuration
const formSteps = [
  { id: 'welcome', title: 'Welcome' },
  { id: 'calculator-check', title: 'Calculator Check', conditional: true },
  { id: 'nmls-prequalification', title: 'NMLS Pre-Qualification' },
  { id: 'non-licensed-identity', title: 'Who are you?', conditional: true },
  { id: 'non-licensed-intent', title: 'What are you looking for?', conditional: true },
  { id: 'non-licensed-timeline', title: 'How soon do you need a solution?', conditional: true },
  { id: 'non-licensed-challenge', title: "What's your biggest challenge?", conditional: true },
  { id: 'nmls-early', title: 'NMLS Number', conditional: true },
  { id: 'nmls-retry', title: 'NMLS Retry', conditional: true },
  { id: 'nmls-loading', title: 'Preparing Questions', conditional: true },
  { id: 'role', title: 'Role' },
  { id: 'volume', title: 'Current Volume' },
  { id: 'team', title: 'Team Size' },
  { id: 'years-in-industry', title: 'Years in Industry' },
  { id: 'budget', title: 'Marketing Budget' },
  { id: 'loan-size', title: 'Loan Size' },
  { id: 'experience', title: 'Experience' },
  { id: 'sources', title: 'Lead Sources', conditional: true },
  { id: 'agency', title: 'Agency Experience' },
  { id: 'crm', title: 'CRM System' },
  { id: 'website-status', title: 'Website Status' },
  { id: 'website-performance', title: 'Website Performance', conditional: true },
  { id: 'website-url', title: 'Website URL', conditional: true },
  { id: 'partnerships', title: 'Agent Partnerships' },
  { id: 'agent-importance', title: 'Agent Partnership Importance', conditional: true },
  { id: 'challenge', title: 'Biggest Challenge' },
  { id: 'lead-gen-approach', title: 'Lead Gen Approach' },
  { id: 'timeline', title: 'Timeline' },
  { id: 'states', title: 'Licensed States' },
  { id: 'nmls-confirm', title: 'NMLS Confirmation', conditional: true },
  { id: 'contact', title: 'Contact Info' },
  { id: 'submitting', title: 'Submitting' },
  { id: 'thankyou', title: 'Complete' },
];

/**
 * Step ID to URL slug mapping
 * Used for query param step tracking: ?step={slug}
 */
const STEP_URL_MAP = {
  'welcome': 'welcome',
  'calculator-check': 'calculator-check',
  'nmls-prequalification': 'nmls-pre-qualification',
  'non-licensed-identity': 'who-are-you',
  'non-licensed-intent': 'what-you-need',
  'non-licensed-timeline': 'how-soon',
  'non-licensed-challenge': 'biggest-challenge',
  'nmls-early': 'nmls-number',
  'nmls-retry': 'nmls-retry',
  'nmls-confirm': 'nmls-confirmation',
  'role': 'role',
  'volume': 'volume',
  'team': 'team-size',
  'years-in-industry': 'years-in-industry',
  'budget': 'marketing-budget',
  'loan-size': 'average-loan-size',
  'experience': 'lead-generation-experience',
  'sources': 'current-lead-sources',
  'agency': 'agency-experience',
  'crm': 'crm-system',
  'website-status': 'website-status',
  'website-performance': 'website-performance',
  'website-url': 'website-url',
  'partnerships': 'agent-partnerships',
  'agent-importance': 'agent-partnership-importance',
  'challenge': 'biggest-challenge',
  'lead-gen-approach': 'lead-gen-approach',
  'timeline': 'timeline',
  'states': 'licensed-states',
  'contact': 'contact-info',
  'submitting': 'submitting',
  'thankyou': 'complete',
} as const;

const getStepIdFromSlug = (slug: string): string | undefined => {
  return Object.keys(STEP_URL_MAP).find(
    key => STEP_URL_MAP[key as keyof typeof STEP_URL_MAP] === slug
  );
};

// US States + DC (alphabetical)
const allUSStates = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois',
  'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts',
  'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota',
  'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

// Top 15 mortgage states by volume (alphabetized)
const topMortgageStates = [
  'Arizona', 'California', 'Florida', 'Georgia', 'Illinois', 'Massachusetts',
  'Michigan', 'New Jersey', 'New York', 'North Carolina', 'Ohio',
  'Pennsylvania', 'Texas', 'Virginia', 'Washington'
];

// Budget formatting helper
const formatBudgetForDisplay = (budget: string): string => {
  const budgetMap: Record<string, string> = {
    '$100 or less': '$100 or less',
    '$101 - $250': '$101-$250',
    '$251 - $500': '$251-$500',
    '$501 - $1,000': '$500-$1K',
    '$1,001 - $2,000': '$1K-$2K',
    '$2,001 - $3,000': '$2K-$3K',
    '$3,001 - $5,000': '$3K-$5K',
    '$5,001 - $10,000': '$5K-$10K',
    '$10,001 - $20,000': '$10K-$20K',
    '$20,001 - $30,000': '$20K-$30K',
    '$30,001 - $50,000': '$30K-$50K',
    'Over $50,000': '$50K+',
  };
  return budgetMap[budget] || 'your monthly budget';
};

// Elegant Loader Component (redesign: the "unlocking your plan" vault; same props, same message)
const ElegantLoader = ({ message, showSuccess = false }: { message: string; showSuccess?: boolean }) => (
  <GMUnlock message={message} showSuccess={showSuccess} />
);

// HubSpot Calendar Component
const HubSpotCalendar = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';
    script.async = true;
    document.body.appendChild(script);

    // MutationObserver to detect when HubSpot injects the iframe
    const observer = new MutationObserver(() => {
      if (containerRef.current) {
        const iframe = containerRef.current.querySelector('iframe');
        if (iframe) {
          // Apply iframe styling once detected
          iframe.style.height = '620px';
          iframe.style.minHeight = '620px';
          // Stop observing once iframe is found
          observer.disconnect();
        }
      }
    });

    // Start observing container for iframe injection
    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true
      });
    }

    // Listen for HubSpot postMessage events (exploratory)
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from HubSpot
      if (event.origin === 'https://meetings.hubspot.com' || event.origin.includes('hsappstatic.net')) {
        console.log('HubSpot postMessage received:', event.data);

        // Log any data to see if they send step navigation info
        // Future: Could resize iframe based on event.data if it contains step info

        // Fire demo_booked event when HubSpot booking is completed
        // HubSpot sends "meetingBookSucceeded" event
        if (event.data && event.data.meetingBookSucceeded) {
          // Google Analytics - demo_booked event
          if (typeof window !== 'undefined' && (window as any).gtag) {
            (window as any).gtag('event', 'demo_booked', {
              event_category: 'Calculator',
              event_label: 'Thank You Page HubSpot Calendar'
            });
          }

          // Facebook Pixel - Schedule event
          if (typeof window !== 'undefined' && (window as any).fbq) {
            (window as any).fbq('track', 'Schedule', {
              content_name: 'Demo Booking'
            });
          }

          console.log('Demo booking event tracked');
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      observer.disconnect();
      window.removeEventListener('message', handleMessage);
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="meetings-iframe-container"
      data-src="https://meetings.hubspot.com/csteverson/demorequest?embed=true"
      style={{ minHeight: '600px', backgroundColor: '#3f53f9' }}
    ></div>
  );
};

// Reveal moment on the NMLS confirm step: the radar scans for at least REVEAL_SCAN_MS, the lock-on ("Success! We found your profile!") then
// lasts REVEAL_LOCK_MS, then the profile card enters. If the data is not ready the radar keeps scanning (no fake extra wait). Reduced motion skips both.
const REVEAL_SCAN_MS = 1200;
const REVEAL_LOCK_MS = 1500;
const revealReduced = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Confetti Celebration Function
const triggerConfetti = () => {
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  const colors = ['#ff34a7', '#2a4afd', '#FFD601', '#34d399', '#a78bfa'];
  const duration = 3000;
  const animationEnd = Date.now() + duration;
  const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

  const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

  const interval = setInterval(() => {
    const timeLeft = animationEnd - Date.now();

    if (timeLeft <= 0) {
      clearInterval(interval);
      return;
    }

    const particleCount = 50 * (timeLeft / duration);

    confetti({
      ...defaults,
      particleCount,
      origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      colors: colors,
    });
    confetti({
      ...defaults,
      particleCount,
      origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      colors: colors,
    });
  }, 250);
};

interface MortgageMarketingDemoFormProps {
  // No props needed - form is self-contained
}

export function MortgageMarketingDemoFormV3() {
  // the PUBLIC url this runs on (the original hard-codes /mortgage-lead-generation-demo) and its variant ('pricing' on /pricing)
  const { path: FORM_BASE, source: FORM_SOURCE } = useContext(DemoBaseContext);
  const router = useRouter();
  const searchParams = useSearchParams();

  // Detect source parameter for pricing variant
  const source = searchParams?.get('source') || FORM_SOURCE;

  // Welcome screen configuration by source
  const welcomeConfig = {
    default: {
      title: "Book Your Growth Strategy Session Now",
      accent: "Growth Strategy",
      subtitle: "Take 60 seconds to answer some simple questions and we'll tailor a personalized strategy to help you achieve your goals.",
      buttonText: "Get My Implementation Plan"
    },
    pricing: {
      title: "Get Pricing That Matches Your Goals",
      accent: "Your Goals",
      subtitle: "Take 60 seconds to answer some simple questions and we'll build a custom pricing package that fits your budget and business needs.",
      buttonText: "Get My Custom Pricing Plan"
    }
  };

  // Final submit button configuration by source
  const finalSubmitConfig = {
    default: "Build My System",
    pricing: "Request Pricing Now"
  };

  // Get current config based on source
  const welcome = welcomeConfig[source as 'default' | 'pricing'] || welcomeConfig.default;
  const finalSubmitText = finalSubmitConfig[source as 'default' | 'pricing'] || finalSubmitConfig.default;

  const [currentStep, setCurrentStep] = useState(0);
  const [showAdjustInputs, setShowAdjustInputs] = useState(false);
  const [adjustmentReason, setAdjustmentReason] = useState<'modify' | 'new'>('modify');
  const [showAllStates, setShowAllStates] = useState(false);
  const [adjustedValues, setAdjustedValues] = useState({
    adSpend: 2500,
    commission: 4000,
    contactRate: 25,
  });
  const websiteUrlInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<FormData>({
    calculatorConfirmed: false,
    nmls: '',
    role: '',
    currentVolume: '',
    teamSize: '',
    yearsInIndustry: '',
    marketingBudget: '',
    avgLoanSize: '',
    leadGenExperience: '',
    currentLeadSources: [],
    agencyExperience: '',
    crmSystem: '',
    websiteStatus: '',
    websitePerformance: '',
    websiteUrl: '',
    agentPartnerships: '',
    agentPartnershipImportance: '',
    biggestChallenge: '',
    leadGenApproach: '',
    systemPreference: '',
    timeline: '',
    primaryState: '',
    additionalStates: [],
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    bestTimeToCall: 'Anytime',
    startedAt: new Date().toISOString(),
    timePerStep: {},
  });

  // Pre-fill from waitlist localStorage (early bird → demo flow)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('waitlist_prefill');
      if (!raw) return;
      const data = JSON.parse(raw);
      // Expire after 30 days
      if (Date.now() - data.timestamp > 30 * 24 * 60 * 60 * 1000) return;
      const parts = (data.name || '').trim().split(/\s+/);
      setFormData(prev => ({
        ...prev,
        ...(parts[0] && { firstName: parts[0] }),
        ...(parts.length > 1 && { lastName: parts.slice(1).join(' ') }),
        ...(data.email && { email: data.email }),
        ...(data.nmls && { nmls: data.nmls }),
      }));
    } catch {}
  }, []);

  const [stepStartTime, setStepStartTime] = useState(Date.now());
  const [loaderAMessage, setLoaderAMessage] = useState('Loading...');
  const [loaderBMessage, setLoaderBMessage] = useState('Processing...');
  const [draftId, setDraftId] = useState<string | null>(null);

  // Phase 2: Pre-qualification State (Task 2.1)
  const [hasNMLSLicense, setHasNMLSLicense] = useState<boolean | null>(null);

  // Non-Licensed User Questions
  const [nonLicensedIdentity, setNonLicensedIdentity] = useState('');
  const [nonLicensedIntent, setNonLicensedIntent] = useState('');
  const [nonLicensedTimeline, setNonLicensedTimeline] = useState('');
  const [nonLicensedChallenge, setNonLicensedChallenge] = useState('');

  // Phase 2: NMLS Validation State (Task B1)
  const [nmlsValidating, setNmlsValidating] = useState(false);
  const [matchStatus, setMatchStatus] = useState<'loading' | 'found' | 'not_found' | 'timeout' | null>(null);
  const [skipVerification, setSkipVerification] = useState(false);
  const [shouldShowNMLSLoading, setShouldShowNMLSLoading] = useState(false);
  const [shouldShowNMLSRetry, setShouldShowNMLSRetry] = useState(false);
  const [showVerificationSuccess, setShowVerificationSuccess] = useState(false);
  const [enrichedData, setEnrichedData] = useState<EnrichedData | null>(null);
  const [nmlsValidationError, setNmlsValidationError] = useState<string | null>(null);
  const [debugResponse, setDebugResponse] = useState<any>(null);
  const [shouldNavigateToContact, setShouldNavigateToContact] = useState(false);

  // Journey tracking state
  const [rejectedProfiles, setRejectedProfiles] = useState<Array<{
    nmls: string;
    data: any;
    rejected_at: string;
    rejection_reason: string;
  }>>([]);
  const [verificationRejected, setVerificationRejected] = useState(false);
  const [scraperPending, setScraperPending] = useState(false);

  // Phase 2: Confirmation State (Task B2)
  const [confirmingNMLS, setConfirmingNMLS] = useState(false);
  const [enriched, setEnriched] = useState(false);

  // No session management needed - scraper runs on NMLS entry

  // B8: Verified Fields State (from VerificationScreen)
  const [verifiedFields, setVerifiedFields] = useState<Array<{ field: string; label: string; value: any; verified: boolean }> | null>(null);

  // B8.1: Checkbox state for verification fields (all checked by default)
  const [verifiedCheckboxes, setVerifiedCheckboxes] = useState<{[key: string]: boolean}>({
    fullName: true,
    nmlsNumber: true,
    company: true,
    title: true,
    totalLoans: true,
    totalVolume: true,
    avgLoanAmount: true,
    purchase: true,
    refinance: true,
    location: true,
    yearsLicensed: true
  });

  // Contact form validation state
  const [fieldValidation, setFieldValidation] = useState({
    firstName: { isValid: false, isTouched: false },
    lastName: { isValid: false, isTouched: false },
    email: { isValid: false, isTouched: false },
    phone: { isValid: false, isTouched: false },
    companyName: { isValid: false, isTouched: false }, // Required field
    bestTimeToCall: { isValid: true, isTouched: true }, // Always valid (dropdown with default)
    websiteUrl: { isValid: false, isTouched: false },
  });

  // Track submitting state for button loading indicator
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  // Submit failure: message shown on the contact step, and a guard so a double click cannot send twice
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submitInFlightRef = useRef(false);
  // Company filled in from the verified profile (drives the hint line and the Change company button)
  const [companyFromProfile, setCompanyFromProfile] = useState(false);
  const companyInputRef = useRef<HTMLInputElement>(null);
  const skipContactAutofillRef = useRef(false);
  // True while the visitor stays on the contact step, so a field they already touched keeps its message
  const contactVisitRef = useRef(false);

  // Track submission completion for success animation and redirect
  const [submissionId, setSubmissionId] = React.useState<string | null>(null);
  const [showSuccessAnimation, setShowSuccessAnimation] = React.useState(false);

  // Ref for NMLS input auto-select
  const nmlsInputRef = useRef<HTMLInputElement>(null);
  const autoAdvanceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Phase 2: NMLS Validation Handler (Task B1 + B2) + B4: Pass NMLS to session
  // The Continue button on the NMLS screen waits for the real lookup, not a fixed timer: it moves on as soon as the lookup has answered,
  // never sooner than NMLS_MIN_WAIT_MS (so "Validating..." is readable) and never later than NMLS_MAX_WAIT_MS (a slow lookup keeps running
  // in the background and the reveal step picks it up). A failed lookup clears autoAdvanceTimerRef, which cancels the advance.
  const NMLS_MIN_WAIT_MS = 1500;
  const NMLS_MAX_WAIT_MS = 3000;
  const NMLS_NOT_FOUND_MESSAGE = "We couldn't find a profile for that NMLS number. Check the number and try again, or continue anyway.";
  const advanceWhenLookupAnswers = (lookupDone: Promise<void>, advance: () => void | Promise<void>) => {
    autoAdvanceTimerRef.current = setTimeout(() => {
      autoAdvanceTimerRef.current = null;
      void advance();
    }, NMLS_MAX_WAIT_MS);
    void Promise.all([new Promise(resolve => setTimeout(resolve, NMLS_MIN_WAIT_MS)), lookupDone]).then(() => {
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
        void advance();
      }
    });
  };

  const handleNMLSValidation = async () => {
    // Prevent double-submission
    if (nmlsValidating) {
      console.log('⚠️ Already validating, ignoring duplicate call');
      return;
    }

    // Generate draftId if it doesn't exist
    let draftIdToUse: string;
    if (!draftId) {
      const newDraftId = `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      draftIdToUse = newDraftId; // Use local variable first (setState is async)
      setDraftId(newDraftId); // Set state after local variable
    } else {
      draftIdToUse = draftId;
    }

    if (!formData.nmls || formData.nmls.length < 4) {
      console.error('❌ DEBUG: NMLS validation failed - length check');
      console.error('❌ DEBUG: formData.nmls =', formData.nmls);
      console.error('❌ DEBUG: length =', formData.nmls?.length);
      return;
    }

    setNmlsValidating(true);
    setNmlsValidationError(null);

    try {
      console.log('🚀 Starting Model-Match scraper for NMLS:', formData.nmls);

      // Dispatch event for debug panel: Scraper started
      window.dispatchEvent(new CustomEvent('scraperUpdate', {
        detail: {
          type: 'scraper_started',
          nmls: formData.nmls,
          draftId: draftIdToUse,
          timestamp: Date.now()
        }
      }));

      console.error('🔍 DEBUG: About to call scraper API');
      console.error('🔍 DEBUG: nmls =', formData.nmls);
      console.error('🔍 DEBUG: draftIdToUse =', draftIdToUse);

      // Fire-and-forget call to original scraper endpoint
      // User continues form immediately, data saves to database in background
      const lookupDone: Promise<void> = fetch('/api/model-match-scraper', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nmls: formData.nmls,
          draftId: draftIdToUse, // Use local variable, not state
          leadSourceType: source === 'pricing' ? 'pricing_form' : 'demo_form' // Pass form source
        })
      }).then(response => {
        console.error('🔍 DEBUG: Fetch started, got response');
        return response.json();
      })
        .then(result => {
          console.log('✅ [Form] Scraper completed:', result);
          if (result.success) {
            console.log('✅ [Form] Data saved to database, lead ID:', result.leadId);
          } else {
            // Handle scraper failure
            console.log('❌ [Form] Scraper failed:', result.error);
            setMatchStatus('not_found');
            setEnrichedData(null);
            setEnriched(false);
            setNmlsValidating(false);
            setNmlsValidationError(NMLS_NOT_FOUND_MESSAGE);
            // Clear auto-advance timer since scraper failed
            if (autoAdvanceTimerRef.current) {
              clearTimeout(autoAdvanceTimerRef.current);
              autoAdvanceTimerRef.current = null;
            }
          }
        })
        .catch(error => {
          console.error('❌ [Form] Scraper failed:', error);
          setMatchStatus('not_found');
          setEnrichedData(null);
          setEnriched(false);
          setNmlsValidating(false);
          setNmlsValidationError(NMLS_NOT_FOUND_MESSAGE);
          // Clear auto-advance timer since scraper failed
          if (autoAdvanceTimerRef.current) {
            clearTimeout(autoAdvanceTimerRef.current);
            autoAdvanceTimerRef.current = null;
          }
        });

      console.log('✅ [Form] Scraper started in background, user can continue form');

      // Detect if this is a retry attempt
      const isRetry = currentStepId === 'nmls-retry';

      if (isRetry) {
        // RETRY MODE: Skip business questions, go directly to contact
        console.log('🔄 RETRY MODE: Will navigate to contact after validation');
        setScraperPending(true);

        advanceWhenLookupAnswers(lookupDone, async () => {
          // Wait for draftId state to update
          await new Promise(resolve => setTimeout(resolve, 100));

          // Trigger navigation via state flag (avoids stale closure)
          setShouldNavigateToContact(true);
          setNmlsValidating(false);
        });

      } else {
        // NORMAL MODE: go straight to the business questions. The old "Building Your Custom Plan" screen is no longer shown: it only
        // covered a slow lookup, and the lookup now answers in a couple of seconds and keeps running while the visitor answers.
        advanceWhenLookupAnswers(lookupDone, async () => {
          await new Promise(resolve => setTimeout(resolve, 100));
          handleAutoAdvance({ nmls: formData.nmls }, 0, draftIdToUse);
        });
      }

    } catch (error) {
      console.error('❌ NMLS validation error:', error);
      setEnrichedData(null);
      setNmlsValidationError('Unable to verify NMLS right now. You can continue anyway.');
      setNmlsValidating(false);
    }
  };

  // Phase 2: NMLS Confirmation Handler (Task B2)
  const handleConfirmNMLS = async () => {
    if (!enrichedData || !draftId) return;

    setConfirmingNMLS(true);

    try {
      // Build verifiedFields array from checkbox state
      const verifiedFieldsArray = Object.entries(verifiedCheckboxes)
        .filter(([_, isChecked]) => isChecked)
        .map(([field, _]) => {
          let value;
          if (field === 'location') {
            value = `${enrichedData.city}, ${enrichedData.primaryState}`;
          } else if (field === 'nmlsNumber') {
            value = formData.nmls;
          } else {
            value = enrichedData[field as keyof EnrichedData];
          }

          // Field labels for display
          const fieldLabels: {[key: string]: string} = {
            fullName: 'Name',
            nmlsNumber: 'NMLS',
            company: 'Company',
            title: 'Title',
            totalLoans: 'Total Loans',
            totalVolume: 'Total Volume',
            avgLoanAmount: 'Average Loan Amount',
            purchase: 'Purchase',
            refinance: 'Refinance',
            location: 'Location',
            yearsLicensed: 'Years Licensed'
          };

          return {
            field: field,
            label: fieldLabels[field] || field,
            value: value,
            verified: true
          };
        });

      // Store for contact form auto-fill
      setVerifiedFields(verifiedFieldsArray);

      const request = {
        draftId,
        nmls: formData.nmls,
        verifiedFields: verifiedFieldsArray
      };

      const response = await fetch('/api/confirm-nmls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });

      const result: ConfirmNMLSResponse = await response.json();

      if (result.success) {
        // Mark as enriched and advance to next step
        setEnriched(true);
        console.log('✅ NMLS confirmed, saved to Supabase:', result.recordId);
        handleAutoAdvance({});
      } else {
        console.error('❌ Confirmation failed:', result.error);
        // Still allow user to continue
        handleAutoAdvance({});
      }
    } catch (error) {
      console.error('❌ Confirmation error:', error);
      // Non-blocking - still allow user to continue
      handleAutoAdvance({});
    } finally {
      setConfirmingNMLS(false);
    }
  };

  const handleRejectNMLS = () => {
    // Save the rejected profile before clearing
    if (enrichedData && formData.nmls) {
      setRejectedProfiles(prev => [...prev, {
        nmls: formData.nmls,
        data: enrichedData,
        rejected_at: new Date().toISOString(),
        rejection_reason: 'user_clicked_not_me'
      }]);
      setVerificationRejected(true);
    }

    // Clear enriched data but keep the NMLS number they entered
    setEnrichedData(null);
    setEnriched(false);
    setMatchStatus(null);
    setNmlsValidationError(null);
    setNmlsValidating(false);
    setSkipVerification(true);

    // Show retry step - navigation will happen in useEffect
    setShouldShowNMLSRetry(true);
  };

  const handleEditNMLS = () => {
    // Track that user rejected "no match" screen
    if (formData.nmls) {
      setRejectedProfiles(prev => [...prev, {
        nmls: formData.nmls,
        data: enrichedData || null,
        rejected_at: new Date().toISOString(),
        rejection_reason: enrichedData ? 'user_clicked_not_me' : 'no_match_found'
      }]);
      if (enrichedData) {
        setVerificationRejected(true);
      }
    }

    setEnrichedData(null);
    setNmlsValidationError(null);
    setNmlsValidating(false);
    setSkipVerification(true);

    // Show retry step - navigation will happen in useEffect
    setShouldShowNMLSRetry(true);
  };

  // Track time per step
  useEffect(() => {
    setStepStartTime(Date.now());
  }, [currentStep]);

  // Track form start when page loads
  useEffect(() => {
    // Google Analytics - form_start event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'form_start', {
        event_category: 'Calculator',
        event_label: 'Progressive Lead Form Opened',
        form_source: source === 'pricing' ? 'pricing' : 'demo'
      });
    }

    // Facebook Pixel - FormStarted event
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('trackCustom', 'FormStarted', {
        content_name: 'Mortgage Calculator Lead Form'
      });
    }
  }, []); // Run once on mount

  // Determine conditional steps
  const shouldShowCalculatorCheck = false; // Page version doesn't have calculator data
  const shouldShowNMLSInput = hasNMLSLicense === true; // Only show NMLS input if user has license
  const shouldShowNMLSConfirm = formData.nmls !== ''; // Show if NMLS was entered (step handles loading state)
  const shouldShowLeadSources = formData.leadGenExperience !== '100% referral/sphere based';
  const shouldShowWebsiteQuestions = formData.websiteStatus === 'I have a website that actively generates leads' ||
                                      formData.websiteStatus === 'I have a website but it needs work';
  // Task 2.3: Show agent-importance step only if user said "Yes" to partnerships
  const shouldShowAgentImportance = formData.agentPartnerships === 'Yes';

  const effectiveSteps = useMemo(() => formSteps.filter(step => {
    // Calculator check
    if (step.id === 'calculator-check' && !shouldShowCalculatorCheck) return false;

    // NMLS steps
    if (step.id === 'nmls-early' && !shouldShowNMLSInput) return false;
    if (step.id === 'nmls-retry' && !shouldShowNMLSRetry) return false;
    if (step.id === 'nmls-confirm' && (!shouldShowNMLSConfirm || skipVerification)) return false;
    if (step.id === 'nmls-loading' && !shouldShowNMLSLoading) return false;

    // Non-licensed path: Show ONLY these 4 questions, skip ALL regular questions
    if (hasNMLSLicense === false) {
      // Allow non-licensed questions, contact, and submission flow
      if (['non-licensed-identity', 'non-licensed-intent', 'non-licensed-timeline', 'non-licensed-challenge', 'contact', 'submitting', 'thankyou'].includes(step.id)) return true;
      // Hide everything else
      return false;
    }

    // Licensed path: Hide non-licensed questions
    if (['non-licensed-identity', 'non-licensed-intent', 'non-licensed-timeline', 'non-licensed-challenge'].includes(step.id)) return false;

    // Regular conditional logic
    if (step.id === 'sources' && !shouldShowLeadSources) return false;
    if (step.id === 'website-performance' && !shouldShowWebsiteQuestions) return false;
    if (step.id === 'website-url' && !shouldShowWebsiteQuestions) return false;
    if (step.id === 'agent-importance' && !shouldShowAgentImportance) return false;

    return true;
  }), [
    shouldShowCalculatorCheck,
    shouldShowNMLSInput,
    shouldShowNMLSRetry,
    shouldShowNMLSConfirm,
    skipVerification,
    shouldShowNMLSLoading,
    shouldShowLeadSources,
    shouldShowWebsiteQuestions,
    shouldShowAgentImportance,
    hasNMLSLicense
  ]);

  // Tells the server the visitor is still here (or has left), so the abandoned-lead email only goes out for people who really left.
  useLeadActivity(draftId, effectiveSteps[currentStep]?.id);

  // Navigate to nmls-retry when reject/edit is clicked
  useEffect(() => {
    if (shouldShowNMLSRetry) {
      const retryIndex = effectiveSteps.findIndex(s => s.id === 'nmls-retry');

      if (retryIndex >= 0) {
        setCurrentStep(retryIndex);

        const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : (searchParams?.toString() || ''));
        params.set('step', 'nmls-retry');
        router.push(`${FORM_BASE}?${params.toString()}`, { scroll: false });
      }
    }
  }, [shouldShowNMLSRetry, effectiveSteps]);

  // Navigate to contact when "I don't remember" is clicked
  useEffect(() => {
    if (shouldNavigateToContact) {
      const contactIndex = effectiveSteps.findIndex(s => s.id === 'contact');

      if (contactIndex >= 0) {
        setCurrentStep(contactIndex);

        const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : (searchParams?.toString() || ''));
        params.set('step', 'contact-info');
        router.push(`${FORM_BASE}?${params.toString()}`, { scroll: false });
      }

      // Reset flag
      setShouldNavigateToContact(false);
    }
  }, [shouldNavigateToContact, effectiveSteps]);

  // Reset adjustment screen when navigating between steps
  // BUT preserve showAdjustInputs when returning to calculator-check step
  useEffect(() => {
    const stepId = effectiveSteps[currentStep]?.id;

    // Only reset showAdjustInputs if we're NOT on the calculator-check step
    if (stepId !== 'calculator-check') {
      setShowAdjustInputs(false);
    }

    // Always reset showAllStates when leaving the states step
    if (stepId !== 'states') {
      setShowAllStates(false);
    }
  }, [currentStep, effectiveSteps]);

  // Reset contact form validation when entering contact step
  // This ensures validation state always matches actual form data
  useEffect(() => {
    const stepId = effectiveSteps[currentStep]?.id;

    if (stepId === 'contact') {
      const keepTouched = contactVisitRef.current;
      contactVisitRef.current = true;
      setFieldValidation(prev => ({
        firstName: {
          isValid: validateFirstName(formData.firstName),
          isTouched: keepTouched ? prev.firstName.isTouched : false
        },
        lastName: {
          isValid: validateLastName(formData.lastName),
          isTouched: keepTouched ? prev.lastName.isTouched : false
        },
        email: {
          isValid: validateEmail(formData.email),
          isTouched: keepTouched ? prev.email.isTouched : false
        },
        phone: {
          isValid: validatePhone(formData.phone),
          isTouched: keepTouched ? prev.phone.isTouched : false
        },
        companyName: {
          isValid: validateCompanyName(formData.companyName),
          isTouched: keepTouched ? prev.companyName.isTouched : false
        },
        bestTimeToCall: {
          isValid: true,
          isTouched: true
        },
        websiteUrl: {
          isValid: validateWebsiteUrl(formData.websiteUrl),
          isTouched: keepTouched ? prev.websiteUrl.isTouched : false
        },
      }));
    } else {
      contactVisitRef.current = false;
    }
  }, [currentStep, effectiveSteps, formData.firstName, formData.lastName, formData.email, formData.phone, formData.companyName, formData.websiteUrl]);

  // Poll database for Model-Match data when user reaches verification step
  useEffect(() => {
    // Calculate currentStepId inline to avoid variable ordering issues
    const currentStepId = effectiveSteps[currentStep]?.id;
    let pollInterval: NodeJS.Timeout | null = null;

    // Only start polling if conditions are met
    if (currentStepId === 'nmls-confirm' && !enrichedData && draftId) {
      setMatchStatus('loading');

      console.log('🔄 [Form] Starting database polling for Model-Match data...');

      const stepLandingTime = Date.now(); // Track when user landed on this step
      const minimumLoadingTime = revealReduced() ? 0 : REVEAL_SCAN_MS; // the radar scans at least this long
      let pollCount = 0;

      // Always poll for 20 seconds to handle race condition between database inserts
      const maxPolls = 40; // 40 polls × 0.5s = 20 seconds

      let dataFound = false;

    // Function to show data after minimum loading time
    const showDataWithMinimumDelay = (data: any) => {
      const elapsedTime = Date.now() - stepLandingTime;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);

      console.log(`✅ [Form] Data found. Elapsed: ${elapsedTime}ms, Waiting: ${remainingTime}ms more`);

      // Dispatch data arrival event for debug panel
      window.dispatchEvent(new CustomEvent('scraperUpdate', {
        detail: {
          type: 'data_complete',
          draftId: draftId,
          data: data,
          timestamp: Date.now()
        }
      }));

      setTimeout(() => {
        setEnrichedData(data);
        setEnriched(true);
        setMatchStatus('found');
      }, remainingTime);
    };

    // Immediate first check (don't wait 2 seconds)
    const checkData = async () => {
      try {
        const response = await fetch(`/api/model-match-data?draftId=${draftId}`);
        const result = await response.json();

        if (result.success && result.found && result.data) {
          console.log('✅ [Form] Model-Match data found on first check');

          // Validate data quality (same validation as polling loop)
          const hasRealData =
            result.data.fullName &&
            result.data.nmlsNumber &&
            // Reject "Null" or "null" strings
            result.data.fullName.toLowerCase() !== 'null' &&
            !result.data.fullName.toLowerCase().includes('null null') &&
            // Reject too-short names (must be at least 3 chars)
            result.data.fullName.trim().length >= 3 &&
            // Reject invalid NMLS (must be at least 4 digits)
            result.data.nmlsNumber.toString().trim().length >= 4 &&
            // Additional check: firstName and lastName if they exist
            (!result.data.firstName || result.data.firstName.toLowerCase() !== 'null') &&
            (!result.data.lastName || result.data.lastName.toLowerCase() !== 'null');

          if (hasRealData) {
            // Valid data - show verification screen
            dataFound = true;
            showDataWithMinimumDelay(result.data);
            return true;
          } else {
            // Invalid data - treat as no match, continue polling
            console.log('⚠️ [Form] Data found but no real match (empty fields - first check)');
            return false;
          }
        }
        return false;
      } catch (error) {
        console.error('❌ [Form] Error checking for data:', error);
        return false;
      }
    };

    // Do immediate check first
    checkData().then((found) => {
      if (found) {
        // Data found immediately, timer will handle showing it after 3s
        return;
      }

      // Data not found, start polling
      pollInterval = setInterval(async () => {
        if (dataFound) {
          if (pollInterval) clearInterval(pollInterval);
          return;
        }

        pollCount++;

        // Dispatch polling event for debug panel
        window.dispatchEvent(new CustomEvent('scraperUpdate', {
          detail: {
            type: 'polling',
            draftId: draftId,
            pollCount: pollCount,
            maxPolls: maxPolls,
            timestamp: Date.now()
          }
        }));

        try {
          const response = await fetch(`/api/model-match-data?draftId=${draftId}`);
          const result = await response.json();

          // Log FULL response to console for inspection
          console.log('🔍 FULL API RESPONSE:', JSON.stringify(result, null, 2));

          setDebugResponse({
            success: result.success,
            found: result.found,
            hasData: !!result.data,
            dataKeys: result.data ? Object.keys(result.data).join(', ') : 'no data',
            timestamp: new Date().toLocaleTimeString(),
            fullResponse: result // Store entire response for debugging
          });

          if (result.success && result.found && result.data) {
            console.log('✅ [Form] Model-Match data loaded from database:', result.data);
            dataFound = true;
            if (pollInterval) clearInterval(pollInterval);

            // Data arrived during polling - show immediately (no minimum delay)
            // Dispatch data arrival event for debug panel
            window.dispatchEvent(new CustomEvent('scraperUpdate', {
              detail: {
                type: 'data_complete',
                draftId: draftId,
                data: result.data,
                timestamp: Date.now()
              }
            }));

            // Check if scraper explicitly failed (no match)
            if (result.data.noMatch) {
              console.log('⚠️ [Form] Scraper completed but no match found');
              setMatchStatus('not_found');
            } else {
              // Check if data contains real match or just empty fields
              const hasRealData =
                result.data.fullName &&
                result.data.nmlsNumber &&
                // Reject "Null" or "null" strings
                result.data.fullName.toLowerCase() !== 'null' &&
                !result.data.fullName.toLowerCase().includes('null null') &&
                // Reject too-short names (must be at least 3 chars)
                result.data.fullName.trim().length >= 3 &&
                // Reject invalid NMLS (must be at least 4 digits)
                result.data.nmlsNumber.toString().trim().length >= 4 &&
                // Additional check: firstName and lastName if they exist
                (!result.data.firstName || result.data.firstName.toLowerCase() !== 'null') &&
                (!result.data.lastName || result.data.lastName.toLowerCase() !== 'null');

              if (hasRealData) {
                // Real match - show verification screen
                setEnrichedData(result.data);
                setEnriched(true);
                setMatchStatus('found');
              } else {
                // Data exists but fields are null - true no match
                console.log('⚠️ [Form] Data found but no real match (empty fields)');
                setMatchStatus('not_found');
              }
            }
          } else if (pollCount >= maxPolls) {
            console.error(`❌ [Form] Polling timeout after ${maxPolls * 0.5} seconds - showing the timeout screen`);
            console.error('❌ [Form] SCRAPER_TIMEOUT:', {
              draftId: draftId,
              nmls: formData.nmls,
              timestamp: Date.now(),
              reason: `No data after ${maxPolls * 0.5} seconds`
            });

            // Dispatch timeout event for debug panel
            window.dispatchEvent(new CustomEvent('scraperUpdate', {
              detail: {
                type: 'polling_timeout',
                draftId: draftId,
                timestamp: Date.now()
              }
            }));

            if (pollInterval) clearInterval(pollInterval);

            // Timeout - the scraper may still be running in the background. Tell the visitor and let them continue to the contact step.
            console.log('⏱️ [Form] Polling timeout - showing the timeout screen, scraper continues in background');
            setMatchStatus('timeout');
          } else {
            console.log(`⏳ [Form] Data not ready yet, continuing to poll... (${pollCount}/${maxPolls})`);
          }
        } catch (error) {
          console.error('❌ [Form] Error polling database:', error);
          if (pollCount >= maxPolls) {
            console.warn('⚠️ [Form] Polling stopped due to errors');
            if (pollInterval) clearInterval(pollInterval);
            setMatchStatus('timeout');
          }
        }
      }, 500); // Poll every 500ms (0.5 seconds)
    });
    }

    // Cleanup ALWAYS runs
    return () => {
      console.log('🛑 [Form] Stopping database polling');
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };
  }, [currentStep, draftId, enrichedData]);

  // Auto-fill contact form from verified profile data
  useEffect(() => {
    const currentStepId = effectiveSteps[currentStep]?.id;

    // Only run when user reaches contact step
    if (currentStepId !== 'contact') return;

    // Only auto-fill if user verified profile
    if (!enriched || !enrichedData) return;

    // The Change company button asked to leave the fields alone for the one re-run its own state change causes
    if (skipContactAutofillRef.current) {
      skipContactAutofillRef.current = false;
      return;
    }

    console.log('🔄 [Form] Auto-filling contact form from verified profile');

    // Check which fields user verified
    const fullNameVerified = verifiedFields?.some(f => f.field === 'fullName' && f.verified) ?? false;
    const companyVerified = verifiedFields?.some(f => f.field === 'company' && f.verified) ?? false;

    // Split fullName into first and last (only if verified)
    const nameParts = fullNameVerified ? (enrichedData.fullName?.split(' ') || []) : [];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    // Auto-fill ONLY verified fields
    setFormData(prev => ({
      ...prev,
      firstName: fullNameVerified ? firstName : '',
      lastName: fullNameVerified ? lastName : '',
      companyName: companyVerified ? (enrichedData.company || '') : ''
      // email, phone, state stay BLANK
    }));

    setCompanyFromProfile(companyVerified && !!enrichedData.company);

    console.log('✅ [Form] Contact form auto-filled:', {
      firstName: fullNameVerified ? firstName : 'not verified',
      lastName: fullNameVerified ? lastName : 'not verified',
      company: companyVerified ? enrichedData.company : 'not verified'
    });

  }, [currentStep, effectiveSteps, enriched, enrichedData, verifiedFields]);

  // Auto-select NMLS text when user clicks "Edit NMLS"
  useEffect(() => {
    const currentStepId = effectiveSteps[currentStep]?.id;

    if (currentStepId === 'nmls-early' && formData.nmls && nmlsInputRef.current) {
      // Small delay to ensure input is focused first
      setTimeout(() => {
        nmlsInputRef.current?.select();
      }, 100);
    }
  }, [currentStep]);

  // Auto-advance from nmls-loading after 4 seconds
  useEffect(() => {
    const currentStepId = effectiveSteps[currentStep]?.id;

    if (currentStepId === 'nmls-loading') {
      const timer = setTimeout(() => {
        handleNext();
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [currentStep, effectiveSteps]);

  // Reveal: the lock-on shows for REVEAL_LOCK_MS, then the card enters. Reduced motion skips the lock-on (the card just fades in). The layout effect
  // switches the lock-on on before paint, so the card never flashes for a frame first; the cleanup resets the flag so leaving the step mid-lock cannot strand it.
  useLayoutEffect(() => {
    if (matchStatus === 'found' && enrichedData && !revealReduced()) {
      setShowVerificationSuccess(true);
      sfx.badge();

      const timer = setTimeout(() => {
        setShowVerificationSuccess(false);
      }, REVEAL_LOCK_MS);

      return () => { clearTimeout(timer); setShowVerificationSuccess(false); };
    }
  }, [matchStatus, enrichedData]);

  // Loader A: Sequenced messages with form data
  useEffect(() => {
    const stepId = effectiveSteps[currentStep]?.id;
    if (stepId !== 'preparing-contact') return;

    console.log('🔄 PREPARING-CONTACT: Auto-advance timer started');

    const formattedBudget = formatBudgetForDisplay(formData.marketingBudget);
    const state = formData.primaryState;

    const messages = [
      `Calculating potential ROI for your ${formattedBudget}/month...`,
      `Analyzing ${state} market dynamics...`,
      `Developing your custom roadmap...`,
    ];
    const timings = [1500, 1800, 1200]; // milliseconds

    let currentMessageIndex = 0;
    setLoaderAMessage(messages[0]);

    const timers: NodeJS.Timeout[] = [];
    let cumulativeTime = timings[0];

    for (let i = 1; i < messages.length; i++) {
      const timer = setTimeout(() => {
        setLoaderAMessage(messages[i]);
      }, cumulativeTime);
      timers.push(timer);
      cumulativeTime += timings[i];
    }

    const autoAdvanceTimer = setTimeout(() => {
      console.log('⏰ PREPARING-CONTACT: Auto-advance timer FIRED - calling handleNext()');
      // Advance to next step directly
      setCurrentStep(prev => {
        const nextStep = prev + 1;
        const nextStepId = effectiveSteps[nextStep]?.id;
        const stepSlug = STEP_URL_MAP[nextStepId as keyof typeof STEP_URL_MAP];
        if (stepSlug) {
          const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : (searchParams?.toString() || ''));
          params.set('step', stepSlug);
          router.push(`${FORM_BASE}?${params.toString()}`, { scroll: false });
        }
        return nextStep;
      });
    }, cumulativeTime);
    timers.push(autoAdvanceTimer);

    return () => {
      console.log('🧹 PREPARING-CONTACT: Cleanup - timers cleared');
      timers.forEach(timer => clearTimeout(timer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, formData.marketingBudget, formData.primaryState]);

  // Auto-focus first empty required field on contact step
  useEffect(() => {
    const currentStepId = effectiveSteps[currentStep]?.id;

    if (currentStepId === 'contact') {
      // Check which fields are empty and focus the first one
      setTimeout(() => {
        if (!formData.firstName && document.getElementById('firstName')) {
          document.getElementById('firstName')?.focus();
        } else if (!formData.lastName && document.getElementById('lastName')) {
          document.getElementById('lastName')?.focus();
        } else if (!formData.email && document.getElementById('email')) {
          document.getElementById('email')?.focus();
        } else if (!formData.companyName && document.getElementById('companyName')) {
          document.getElementById('companyName')?.focus();
        }
      }, 100);
    }
  }, [currentStep, effectiveSteps, formData.firstName, formData.lastName, formData.email, formData.companyName]);

  // Loader B: Generic sequenced messages
  useEffect(() => {
    const stepId = effectiveSteps[currentStep]?.id;
    if (stepId !== 'submitting') return;

    const messages = [
      'Securing your information...',
      'Matching you with a Marketing Advisor...',
      'Your strategy session is ready to book...',
    ];
    const timings = [1500, 2000, 1500]; // milliseconds

    setLoaderBMessage(messages[0]);

    const timers: NodeJS.Timeout[] = [];
    let cumulativeTime = timings[0];

    for (let i = 1; i < messages.length; i++) {
      const timer = setTimeout(() => {
        setLoaderBMessage(messages[i]);
      }, cumulativeTime);
      timers.push(timer);
      cumulativeTime += timings[i];
    }

    // Don't redirect here - just wait for submission to complete
    // Redirect will be triggered by separate useEffect watching submissionId
    const showSuccessTimer = setTimeout(() => {
      console.log('✨ Loader messages complete - waiting for API submission');
      setLoaderBMessage('Finalizing your submission...');
    }, cumulativeTime);
    timers.push(showSuccessTimer);

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Watch for submissionId and trigger redirect when ready
  useEffect(() => {
    if (submissionId !== null && effectiveSteps[currentStep]?.id === 'submitting') {
      console.log('✅ Submission complete! Showing success and redirecting');
      setShowSuccessAnimation(true);
      setLoaderBMessage('Success! Redirecting to your plan...');

      // Wait 500ms, then redirect
      setTimeout(() => {
        console.log('🔗 Redirecting to thank-you page');
        const redirectUrl = submissionId
          ? `${FORM_BASE}/thank-you?sid=${submissionId}`
          : `${FORM_BASE}/thank-you`;
        console.log('🔗 Redirect URL:', redirectUrl);
        window.location.href = redirectUrl;
      }, 500);
    }
  }, [submissionId, currentStep, effectiveSteps]);

  // Confetti trigger when thank you page appears
  useEffect(() => {
    const stepId = effectiveSteps[currentStep]?.id;
    if (stepId === 'thankyou') {
      const confettiTimer = setTimeout(() => {
        triggerConfetti();
      }, 300);
      return () => clearTimeout(confettiTimer);
    }
  }, [currentStep, effectiveSteps]);

  // Track maximum step reached for security (prevent URL jumping ahead)
  const [maxStepReached, setMaxStepReached] = React.useState(0);

  // URL validation on mount - only block if directly accessing ahead of progress
  useEffect(() => {
    const urlStep = searchParams.get('step');

    if (!urlStep) {
      // No step param - start at welcome
      const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : (searchParams?.toString() || ''));
      params.set('step', 'welcome');
      router.replace(`${FORM_BASE}?${params.toString()}`);
      setCurrentStep(0);
      return;
    }

    // If this is a browser session and they're starting fresh, go to welcome
    // But allow browser back/forward to work for steps they've already seen
    if (urlStep !== 'welcome' && maxStepReached === 0) {
      // First time loading the form with a mid-form URL - redirect to start
      const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : (searchParams?.toString() || ''));
      params.set('step', 'welcome');
      router.replace(`${FORM_BASE}?${params.toString()}`);
      setCurrentStep(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount only

  // Update maxStepReached whenever currentStep increases
  useEffect(() => {
    if (currentStep > maxStepReached) {
      setMaxStepReached(currentStep);
    }
  }, [currentStep, maxStepReached]);

  // B8: Auto-fill contact form with ONLY verified fields (firstName, lastName, company)
  useEffect(() => {
    const currentStepId = effectiveSteps[currentStep]?.id;

    // Only auto-fill when reaching contact step AND verified fields exist
    if (currentStepId !== 'contact' || !verifiedFields || verifiedFields.length === 0) return;

    // Check if fields are already populated (avoid overwriting user edits on step re-entry)
    const hasExistingData = formData.firstName || formData.lastName || formData.companyName;
    if (hasExistingData) return;

    console.log('✨ B8: Auto-filling contact info from verifiedFields (ONLY name + company):', verifiedFields);

    // CRITICAL: ONLY extract fullName and company from verified fields
    // Filter to ONLY fields that were checked by user
    const verifiedOnly = verifiedFields.filter(f => f.verified);

    // Extract fullName and company (ONLY these 2 fields)
    const fullNameField = verifiedOnly.find(f => f.field === 'fullName');
    const companyField = verifiedOnly.find(f => f.field === 'company');

    // Split fullName into firstName + lastName
    let firstName = '';
    let lastName = '';
    if (fullNameField?.value) {
      const nameParts = fullNameField.value.split(' ');
      firstName = nameParts[0] || '';
      lastName = nameParts.slice(1).join(' ') || '';
    }

    const company = companyField?.value || '';

    console.log('✨ B8: Extracted for auto-fill:', { firstName, lastName, company });
    console.log('❌ B8: Email, phone, state NEVER auto-fill (user must enter)');

    // Update formData (ONLY firstName, lastName, company)
    // NEVER auto-fill: email, phone, primaryState
    setFormData(prev => ({
      ...prev,
      firstName: firstName,
      lastName: lastName,
      companyName: company,
      // Explicitly NOT setting: email, phone, primaryState
    }));

    // Update field validation for auto-filled fields
    if (firstName) {
      setFieldValidation(prev => ({
        ...prev,
        firstName: {
          isValid: true,
          isTouched: false,
        },
      }));
    }

    if (lastName) {
      setFieldValidation(prev => ({
        ...prev,
        lastName: {
          isValid: true,
          isTouched: false,
        },
      }));
    }

    if (company) {
      setCompanyFromProfile(true);
      setFieldValidation(prev => ({
        ...prev,
        companyName: {
          isValid: true,
          isTouched: false,
        },
      }));
    }
  }, [currentStep, verifiedFields, effectiveSteps, formData.firstName, formData.lastName, formData.companyName]);

  // Task 2.3: Calculate extractAgents flag and send to backend
  useEffect(() => {
    // Only run after both partnership questions are answered
    if (!formData.agentPartnerships || !formData.agentPartnershipImportance) return;

    // If user said "No" to partnerships, extractAgents = false
    if (formData.agentPartnerships === 'No') {
      console.log('📊 Task 2.3: extractAgents = false (no partnerships)');
      return;
    }

    // Calculate extractAgents based on importance level
    const extractAgents =
      formData.agentPartnershipImportance === 'Very important' ||
      formData.agentPartnershipImportance === 'Important';

    console.log('📊 Task 2.3: Agent partnerships detected', {
      agentPartnerships: formData.agentPartnerships,
      importance: formData.agentPartnershipImportance,
      extractAgents: extractAgents
    });

    // TODO: Uncomment when Task 3.3 complete (Code A builds /api/enrich-lead-async/update endpoint)
    /*
    const updateExtractAgents = async () => {
      if (!draftId && !enriched) return; // Need leadId to update

      try {
        const response = await fetch('/api/enrich-lead-async/update', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            leadId: draftId, // or use actual leadId if available
            extractAgents: extractAgents
          })
        });

        const result = await response.json();

        if (result.success) {
          console.log('✅ Task 2.3: extractAgents flag updated successfully');
        } else {
          console.error('❌ Task 2.3: Failed to update extractAgents:', result.error);
        }
      } catch (error) {
        console.error('❌ Task 2.3: Error updating extractAgents:', error);
      }
    };

    updateExtractAgents();
    */
  }, [formData.agentPartnerships, formData.agentPartnershipImportance, draftId, enriched]);

  // Browser back/forward button support
  useEffect(() => {
    const handlePopState = () => {
      const urlStep = searchParams.get('step');
      const stepId = getStepIdFromSlug(urlStep || '');

      if (stepId) {
        const stepIndex = effectiveSteps.findIndex(s => s.id === stepId);

        // Only navigate if step is valid and not ahead of max progress
        if (stepIndex >= 0 && stepIndex <= maxStepReached) {
          console.log('🔙 Browser back/forward - navigating to step:', stepIndex, stepId);
          setCurrentStep(stepIndex);
        } else {
          // Invalid or future step - redirect to current
          console.log('🚫 Browser back/forward - blocked future step:', stepIndex, stepId);
          const currentStepId = effectiveSteps[currentStep]?.id;
          const currentSlug = STEP_URL_MAP[currentStepId as keyof typeof STEP_URL_MAP];
          const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : (searchParams?.toString() || ''));
          params.set('step', currentSlug);
          router.replace(`${FORM_BASE}?${params.toString()}`);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentStep, maxStepReached, searchParams, router, effectiveSteps]);

  const recordStepTime = () => {
    const timeSpent = Date.now() - stepStartTime;
    setFormData(prev => ({
      ...prev,
      timePerStep: {
        ...prev.timePerStep,
        [currentStep]: timeSpent,
      },
    }));
  };

  // Get the answer value for current step
  const getStepAnswer = (stepId: string): string | string[] | undefined => {
    switch (stepId) {
      case 'nmls-early': return formData.nmls;
      case 'role': return formData.role;
      case 'volume': return formData.currentVolume;
      case 'team': return formData.teamSize;
      case 'years-in-industry': return formData.yearsInIndustry;
      case 'budget': return formData.marketingBudget;
      case 'loan-size': return formData.avgLoanSize;
      case 'experience': return formData.leadGenExperience;
      case 'sources': return formData.currentLeadSources;
      case 'agency': return formData.agencyExperience;
      case 'crm': return formData.crmSystem;
      case 'website-status': return formData.websiteStatus;
      case 'website-performance': return formData.websitePerformance;
      case 'website-url': return formData.websiteUrl;
      case 'partnerships': return formData.agentPartnerships;
      case 'agent-importance': return formData.agentPartnershipImportance;
      case 'challenge': return formData.biggestChallenge;
      case 'lead-gen-approach': return formData.leadGenApproach;
      case 'preference': return formData.systemPreference;
      case 'timeline': return formData.timeline;
      case 'states': return formData.primaryState;
      default: return undefined;
    }
  };

  // Track step completion with answer data
  const trackStepComplete = (stepId: string, stepTitle: string, stepNumber: number) => {
    // Skip system steps (no user input)
    const skipSteps = ['welcome', 'calculator-check', 'preparing-contact', 'submitting', 'thankyou', 'contact'];
    if (skipSteps.includes(stepId)) return;

    const answer = getStepAnswer(stepId);
    if (!answer) return;

    // Format answer (handle arrays like currentLeadSources)
    const formattedAnswer = Array.isArray(answer) ? answer.join(', ') : answer;

    // Google Analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      const stepSlug = STEP_URL_MAP[stepId as keyof typeof STEP_URL_MAP];
      (window as any).gtag('event', 'form_step_complete', {
        event_category: 'Calculator',
        event_label: stepTitle,
        step_id: stepId,
        step_name: stepSlug,
        step_number: stepNumber,
        page_path: `${FORM_BASE}?step=${stepSlug}`,
        answer: formattedAnswer
      });
    }

    // Facebook Pixel
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('trackCustom', 'FormStepComplete', {
        step_name: stepTitle,
        step_id: stepId,
        step_number: stepNumber,
        answer: formattedAnswer
      });
    }
  };

  const totalSteps = effectiveSteps.length;

  const handleNext = () => {
    const currentStepId = effectiveSteps[currentStep]?.id;
    const currentStepTitle = effectiveSteps[currentStep]?.title;

    // Track step completion with answer data
    if (currentStepId && currentStepTitle) {
      trackStepComplete(currentStepId, currentStepTitle, currentStep);
    }

    recordStepTime();
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);

      // Update URL with new step
      const nextStepId = effectiveSteps[nextStep]?.id;
      const stepSlug = STEP_URL_MAP[nextStepId as keyof typeof STEP_URL_MAP];
      if (stepSlug) {
        const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : (searchParams?.toString() || ''));
        params.set('step', stepSlug);
        router.push(`${FORM_BASE}?${params.toString()}`, { scroll: false });
      }
    }
  };

  const handleBack = () => {
    const stepId = effectiveSteps[currentStep]?.id;

    // Special handling for contact step after verification
    if (stepId === 'contact' && enrichedData) {
      // User verified their NMLS, show verification screen again
      const verificationIndex = effectiveSteps.findIndex(s => s.id === 'nmls-confirm');
      if (verificationIndex >= 0) {
        setCurrentStep(verificationIndex);
        const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : (searchParams?.toString() || ''));
        params.set('step', STEP_URL_MAP['nmls-confirm']);
        router.push(`${FORM_BASE}?${params.toString()}`, { scroll: false });
        return;
      }
    }

    // Special handling for calculator-check step with adjustment screen showing
    if (stepId === 'calculator-check' && showAdjustInputs) {
      // Go back to the 3-option view, don't change step
      setShowAdjustInputs(false);
      return;
    }

    // Normal back behavior
    recordStepTime();
    if (currentStep > 0) {
      let targetStep = currentStep - 1;

      // Skip over auto-advancing loader steps (preparing-contact, submitting)
      // Otherwise user gets trapped in loader → contact loop with no back button
      const targetStepId = effectiveSteps[targetStep]?.id;
      if (targetStepId === 'preparing-contact' || targetStepId === 'submitting') {
        targetStep = targetStep - 1;
      }

      const previousStep = Math.max(0, targetStep);
      setCurrentStep(previousStep);

      // Update URL with previous step
      const previousStepId = effectiveSteps[previousStep]?.id;
      const stepSlug = STEP_URL_MAP[previousStepId as keyof typeof STEP_URL_MAP];
      if (stepSlug) {
        const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : (searchParams?.toString() || ''));
        params.set('step', stepSlug);
        router.push(`${FORM_BASE}?${params.toString()}`, { scroll: false });
      }
    }
  };

  const handleAutoAdvance = (fieldUpdate: Partial<FormData>, delay = 300, existingDraftId?: string) => {
    const currentStepId = effectiveSteps[currentStep]?.id;
    const currentStepTitle = effectiveSteps[currentStep]?.title;

    // Track step completion with answer data
    if (currentStepId && currentStepTitle) {
      trackStepComplete(currentStepId, currentStepTitle, currentStep);
    }

    // Special handling for lead gen experience question
    if (fieldUpdate.leadGenExperience === '100% referral/sphere based') {
      // Auto-set currentLeadSources to ['none'] when they're 100% referral
      setFormData(prev => ({ ...prev, ...fieldUpdate, currentLeadSources: ['none'] }));
    } else {
      setFormData(prev => ({ ...prev, ...fieldUpdate }));
    }

    // BACKGROUND ENRICHMENT: Never create draftId here - it's already created in handleNMLSValidation
    // This block was causing duplicate draftIds - now it's completely removed
    if (currentStepId === 'nmls-early' && fieldUpdate.nmls && existingDraftId) {
      console.log('✅ DEBUG: Using existingDraftId from handleNMLSValidation:', existingDraftId);
      // existingDraftId was already set by handleNMLSValidation, no action needed
    }

    setTimeout(() => {
      handleNext();
    }, delay);
  };

  const handleMultiSelectToggle = (field: 'currentLeadSources', value: string) => {
    const currentValues = formData[field];
    let newValues: string[];

    // Special handling for "None currently"
    if (value === 'none') {
      // If clicking "None", clear all other selections and only set "none"
      newValues = currentValues.includes('none') ? [] : ['none'];
    } else {
      // If clicking any other option, remove "None" if it exists
      const withoutNone = currentValues.filter(v => v !== 'none');
      newValues = withoutNone.includes(value)
        ? withoutNone.filter(v => v !== value)
        : [...withoutNone, value];
    }

    setFormData(prev => ({ ...prev, [field]: newValues }));
  };

  const handleAdditionalStateToggle = (state: string) => {
    const currentStates = formData.additionalStates;
    const newStates = currentStates.includes(state)
      ? currentStates.filter(s => s !== state)
      : [...currentStates, state];
    setFormData(prev => ({ ...prev, additionalStates: newStates }));
  };

  // A failed save puts the visitor back on the contact step with a plain message. Their answers stay and the button is live again.
  const returnToContactWithError = () => {
    submitInFlightRef.current = false;
    setIsSubmitting(false);
    setSubmitError('Your request did not go through. Your answers are still here. Try again in a moment.');
    const contactIndex = effectiveSteps.findIndex(s => s.id === 'contact');
    if (contactIndex >= 0) {
      setCurrentStep(contactIndex);
      const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : (searchParams?.toString() || ''));
      params.set('step', STEP_URL_MAP['contact']);
      router.push(`${FORM_BASE}?${params.toString()}`, { scroll: false });
    }
  };

  const handleSubmit = async () => {
    // A second click while the first is still sending does nothing
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setSubmitError(null);
    console.log('🚀🚀🚀 HANDLESUBMIT CALLED - ENTRY POINT (Phase 2)');

    // Advance to submitting step immediately for visual feedback
    recordStepTime();
    handleNext(); // This advances to 'submitting' step and shows loader

    try {
      console.log('🚀 Starting Phase 2 submission process');

      // Phase 2: Use enriched flag from React state, NOT localStorage
      const completedData = {
        ...formData,
        completedAt: new Date().toISOString(),
        // leadScore: calculateLeadScore(formData), // Lead scoring disabled - needs rebuild
        enriched: enriched, // Phase 2: Use React state
        scraperPending: scraperPending, // Tells backend scraper is running (separate from enriched)
        modelMatchData: enrichedData, // Phase 2: Use React state
        draftId: draftId || null,
        // Journey tracking
        verificationRejected: verificationRejected,
        rejectedProfiles: rejectedProfiles,
        // Non-licensed fields (stored in separate state variables, not in formData)
        nonLicensedIntent,
        nonLicensedIdentity,
        nonLicensedTimeline,
        nonLicensedChallenge,
        // Lead source tracking for form variants
        lead_source_type: source === 'pricing' ? 'pricing_form' : 'demo_form',
      };

      console.log('=== FORM SUBMISSION ===');
      // console.log('Lead Score:', completedData.leadScore); // Lead scoring disabled
      console.log('Form Data:', completedData);

      // TODO: HubSpot submission
      // await submitToHubSpot(completedData);

      // The team email is sent by /api/submit-form after the lead is saved (it carries the routing decision and the looked-up profile).
      // This browser copy is only the safety net: it goes out when the save fails, so a lead is never lost silently.
      const sendFallbackNotification = async () => {
        try {
          const response = await fetch('/api/send-form-notification', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              formData: completedData,
              metadata: {
                submittedAt: completedData.completedAt,
                totalTime: Object.values(completedData.timePerStep).reduce((a, b) => a + b, 0),
                timePerStep: completedData.timePerStep,
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
                viewportWidth: typeof window !== 'undefined' ? window.innerWidth : undefined,
                touch: typeof navigator !== 'undefined' ? navigator.maxTouchPoints : undefined,
                referrer: typeof document !== 'undefined' ? document.referrer : undefined,
              }
            })
          });
          if (!response.ok) {
            console.error('Email notification failed:', await response.json());
          }
        } catch (error) {
          console.error('Failed to send email notification:', error);
        }
      };

    // Zapier webhook now handled server-side in /api/submit-form
    // This ensures routing_decision, public_id, lead_source_type, and non-licensed fields are included
    console.log('ℹ️ Zapier webhook will be sent server-side after database save');

    // Fire tracking pixel events (restored without lead scoring dependency)

    // Google Analytics - generate_lead event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'generate_lead', {
        event_category: 'Demo Request Form',
        event_label: 'Mortgage Marketing Demo',
        marketing_budget: completedData.marketingBudget,
        user_role: completedData.role,
        current_volume: completedData.currentVolume,
        form_source: source === 'pricing' ? 'pricing' : 'demo'
      });
    }

    // Facebook Pixel - Lead event
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'Lead', {
        content_name: 'Mortgage Marketing Demo Form',
        content_category: 'Demo Request'
      });
    }

    // LinkedIn Insight Tag - Lead event (form-specific only)
    if (typeof window !== 'undefined' && (window as any).lintrk) {
      (window as any).lintrk('track', { conversion_id: 7921932 });
    }

    // HubSpot - Lead identification
    if (typeof window !== 'undefined' && (window as any)._hsq) {
      (window as any)._hsq.push(['identify', {
        email: completedData.email,
        firstname: completedData.firstName,
        lastname: completedData.lastName
      }]);
    }

    // Debug logging (only when debug tools enabled)
    if (process.env.NEXT_PUBLIC_ENABLE_DEBUG_TOOLS === 'true') {
      console.log('📊 Tracking Events Fired:', {
        gtag: typeof (window as any)?.gtag !== 'undefined' ? '✅' : '❌',
        fbq: typeof (window as any)?.fbq !== 'undefined' ? '✅' : '❌',
        lintrk: typeof (window as any)?.lintrk !== 'undefined' ? '✅' : '❌',
        hsq: typeof (window as any)?._hsq !== 'undefined' ? '✅' : '❌'
      });
    }

    setFormData(completedData);

    // Phase 2: Call new /api/submit-form endpoint (Session A)
    try {
      // B9: Log verification data being submitted
      if (verifiedFields && verifiedFields.length > 0) {
        console.log('✅ B9: Submitting verification_data with form:', {
          profile_shown: true,
          fields_presented_count: verifiedFields.length,
          verified_fields_count: verifiedFields.filter(f => f.verified).length,
          verified_at: new Date().toISOString()
        });
      }

      const submitResponse = await fetch('/api/submit-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draftId: draftId!,
          formData: {
            // Use formData directly (updated by contact step), not completedData snapshot
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            state: formData.primaryState,
            company: formData.companyName,
            nmls: formData.nmls,
          },
          enriched: enriched,
          scraperPending: scraperPending,
          verificationRejected: verificationRejected,
          rejectedProfiles: rejectedProfiles,
          // B9: Add verification data from VerificationScreen
          verification_data: verifiedFields && verifiedFields.length > 0 ? {
            profile_shown: true,
            fields_presented: verifiedFields,
            verified_at: new Date().toISOString()
          } : null,
          // Metadata for Zapier webhook
          metadata: {
            totalTime: Object.values(completedData.timePerStep).reduce((a, b) => a + b, 0),
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
            viewportWidth: typeof window !== 'undefined' ? window.innerWidth : undefined,
            touch: typeof navigator !== 'undefined' ? navigator.maxTouchPoints : undefined,
            referrer: typeof document !== 'undefined' ? document.referrer : undefined,
          },
          formResponses: {
            role: completedData.role,
            currentVolume: completedData.currentVolume,
            teamSize: completedData.teamSize,
            yearsInIndustry: completedData.yearsInIndustry,
            marketingBudget: completedData.marketingBudget,
            avgLoanSize: completedData.avgLoanSize,
            leadGenExperience: completedData.leadGenExperience,
            currentLeadSources: completedData.currentLeadSources,
            agencyExperience: completedData.agencyExperience,
            crmSystem: completedData.crmSystem,
            websiteStatus: completedData.websiteStatus,
            websitePerformance: completedData.websitePerformance,
            websiteUrl: completedData.websiteUrl,
            agentPartnerships: completedData.agentPartnerships,
            leadGenApproach: completedData.leadGenApproach,
            agentPartnershipImportance: completedData.agentPartnershipImportance,
            biggestChallenge: completedData.biggestChallenge,
            systemPreference: completedData.systemPreference,
            timeline: completedData.timeline,
            primaryState: completedData.primaryState,
            additionalStates: completedData.additionalStates,
            bestTimeToCall: completedData.bestTimeToCall,
            // Non-Licensed User Questions
            nonLicensedIdentity,
            nonLicensedIntent,
            nonLicensedTimeline,
            nonLicensedChallenge,
            // Lead source tracking for form variants
            lead_source_type: source === 'pricing' ? 'pricing_form' : 'demo_form',
            // leadScore: completedData.leadScore, // Lead scoring disabled - needs rebuild
          },
        }),
      });

      const submitResult = await submitResponse.json();
      console.log('✅ Phase 2 submission result:', submitResult);

      if (submitResult.success) {
        // Store in localStorage for loading screen (Phase 2 format)
        localStorage.setItem(`form_submission_${draftId}`, JSON.stringify({
          draftId: draftId,
          enriched: enriched,
          formData: {
            // Use formData directly (updated by contact step), not completedData snapshot
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            state: formData.primaryState,
            company: formData.companyName,
            nmls: formData.nmls,
          },
          modelMatchData: enrichedData,
          formResponses: completedData,
          timestamp: Date.now(),
        }));

        console.log('✅ Phase 2 localStorage saved:', draftId);

        // Get routing decision, public_id, and prefill URL from response
        const routingDecision = submitResult.routingDecision || 'success';
        const publicId = submitResult.publicId || draftId;
        const prefillUrl: string | undefined = submitResult.prefillUrl;

        // Feature flag: Profile pages vs Thank You page
        const ENABLE_PROFILE_PAGES = false;

        if (ENABLE_PROFILE_PAGES) {
          window.location.href = `/results/${publicId}`;
        } else {
          const params = new URLSearchParams({ sid: publicId, route: routingDecision });
          if (prefillUrl) params.set('prefill', prefillUrl);
          window.location.href = `${FORM_BASE}/thank-you?${params.toString()}`;
        }
        return;
      } else {
        console.error('❌ Phase 2 submission failed:', submitResult.error);
        void sendFallbackNotification();

        // Back to the contact step with a plain message (the fallback notification above already went out)
        returnToContactWithError();
        return;
      }
    } catch (error) {
      console.error('❌ Phase 2 submission error:', error);
      void sendFallbackNotification();

      // Back to the contact step with a plain message (the fallback notification above already went out)
      returnToContactWithError();
      return;
    }
  } catch (error) {
    // Outer catch block for any errors in the entire handleSubmit flow
    console.error('❌ CRITICAL ERROR IN HANDLESUBMIT:', error);
    // Fallback: Still redirect to loading screen
    window.location.href = `/profile/${draftId}/loading`;
  }
  };

  const resetAndClose = () => {
    setCurrentStep(0);
    setShowAdjustInputs(false);
    setShowAllStates(false);
    setFormData({
      calculatorConfirmed: false,
      role: '',
      currentVolume: '',
      teamSize: '',
      yearsInIndustry: '',
      marketingBudget: '',
      avgLoanSize: '',
      leadGenExperience: '',
      currentLeadSources: [],
      agencyExperience: '',
      crmSystem: '',
      websiteStatus: '',
      websitePerformance: '',
      websiteUrl: '',
      agentPartnerships: '',
      nmls: '',
      leadGenApproach: '',
      agentPartnershipImportance: '',
      biggestChallenge: '',
      systemPreference: '',
      timeline: '',
      primaryState: '',
      additionalStates: [],
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      companyName: '',
      bestTimeToCall: '',
      startedAt: new Date().toISOString(),
      timePerStep: {},
    });
    // Page version - no onClose needed
  };

  // Validation helpers
  const isStepValid = () => {
    const stepId = effectiveSteps[currentStep]?.id;
    switch (stepId) {
      case 'welcome':
        return true; // Always valid
      case 'calculator-check':
        return true; // Always valid, they choose an option
      case 'nmls-prequalification':
        return hasNMLSLicense !== null; // User must select Yes or No
      case 'role':
        return formData.role !== '';
      case 'volume':
        return formData.currentVolume !== '';
      case 'team':
        return formData.teamSize !== '';
      case 'budget':
        return formData.marketingBudget !== '';
      case 'loan-size':
        return formData.avgLoanSize !== '';
      case 'experience':
        return formData.leadGenExperience !== '';
      case 'sources':
        return formData.currentLeadSources.length > 0;
      case 'agency':
        return formData.agencyExperience !== '';
      case 'crm':
        return formData.crmSystem !== '';
      case 'website-status':
        return formData.websiteStatus !== '';
      case 'website-performance':
        return formData.websitePerformance !== '';
      case 'website-url':
        return fieldValidation.websiteUrl.isValid;
      case 'partnerships':
        return formData.agentPartnerships !== '';
      case 'challenge':
        return formData.biggestChallenge !== '';
      case 'preference':
        return formData.systemPreference !== '';
      case 'timeline':
        return formData.timeline !== '';
      case 'states':
        return formData.primaryState !== '';
      case 'contact':
        const isValid = fieldValidation.firstName.isValid &&
               fieldValidation.lastName.isValid &&
               fieldValidation.email.isValid &&
               fieldValidation.phone.isValid &&
               fieldValidation.companyName.isValid &&
               fieldValidation.bestTimeToCall.isValid;

        console.log('📋 CONTACT VALIDATION:', {
          isValid,
          firstName: fieldValidation.firstName.isValid,
          lastName: fieldValidation.lastName.isValid,
          email: fieldValidation.email.isValid,
          phone: fieldValidation.phone.isValid,
          companyName: fieldValidation.companyName.isValid,
          bestTimeToCall: fieldValidation.bestTimeToCall.isValid,
          formData: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            companyName: formData.companyName
          }
        });

        return isValid;
      default:
        return true;
    }
  };

  const currentStepId = effectiveSteps[currentStep]?.id;

  // Phone number formatter - formats as (XXX) XXX-XXXX
  // Auto-strips leading "1" if user pastes "+1 (619) 886-6985"
  const formatPhoneNumber = (value: string): string => {
    let cleaned = value.replace(/\D/g, '');

    // Strip leading "1" if present and we have more than 10 digits
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      cleaned = cleaned.substring(1);
    }

    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return value;

    const [, p1, p2, p3] = match;
    if (p3) return `(${p1}) ${p2}-${p3}`;
    if (p2) return `(${p1}) ${p2}`;
    if (p1) return p1.length === 3 ? `(${p1}) ` : p1;
    return '';
  };

  // First name formatter - capitalizes first letter, lowercases rest
  const formatFirstName = (name: string): string => {
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  // Validation functions for contact form
  const validateFirstName = (value: string): boolean => {
    return /^[a-zA-ZÀ-ÿ\s.'\-]{2,}$/.test(value);
  };

  const validateLastName = (value: string): boolean => {
    return /^[a-zA-ZÀ-ÿ\s.'\-]{2,}$/.test(value);
  };

  // Shared with /api/submit-form (src/lib/contact-validation.ts) so the browser and the server apply the same rules
  const validateEmail = (value: string): boolean => validateEmailShared(value);

  const validatePhone = (value: string): boolean => validatePhoneShared(value);

  const validateCompanyName = (value: string): boolean => {
    return value.length >= 2; // Required - must be at least 2 characters
  };

  // The Change company button shows only while the company is the one filled in from the verified profile (not empty, not typed by hand)
  const showChangeCompany = companyFromProfile && formData.companyName.length > 0;

  const handleChangeCompany = () => {
    // Stop the two auto-fill effects from putting the profile value back
    skipContactAutofillRef.current = true;
    setVerifiedFields(prev => (prev ? prev.map(f => (f.field === 'company' ? { ...f, verified: false } : f)) : prev));
    setCompanyFromProfile(false);
    setFormData(prev => ({ ...prev, companyName: '' }));
    setFieldValidation(prev => ({ ...prev, companyName: { isValid: validateCompanyName(''), isTouched: false } }));
    window.setTimeout(() => companyInputRef.current?.focus(), 0);
  };

  const validateWebsiteUrl = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) return false;

    // Remove protocol
    let domain = trimmed.replace(/^https?:\/\//, '');

    // Remove www. if present
    domain = domain.replace(/^www\./, '');

    // Remove path/port/query
    domain = domain.split(/[\/\?#:]/)[0];

    // Split by dots
    const parts = domain.split('.');

    // Must have at least 2 parts (domain + TLD)
    if (parts.length < 2) return false;

    // Last part is TLD, must be 2+ letters
    const tld = parts[parts.length - 1];
    if (!/^[a-zA-Z]{2,}$/.test(tld)) return false;

    // All parts before TLD must be valid (alphanumeric/hyphen, not start/end with hyphen)
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      // Allow single char OR multiple chars with valid pattern
      if (!/^[a-zA-Z0-9][a-zA-Z0-9\-]{0,}[a-zA-Z0-9]$/.test(part) && !/^[a-zA-Z0-9]$/.test(part)) {
        return false;
      }
    }

    // Check if domain part (before TLD) has at least 3 chars total
    const domainPart = parts.slice(0, -1).join('.');
    if (domainPart.length < 3) return false;

    return true;
  };

  // Security Badge Component
  const SecurityBadge = () => (
    <div className="gm-secure">
      <Lock className="w-3 h-3" />
      <span>Your Information is Kept Private & Secure</span>
    </div>
  );

  // Add custom styles for PrimeReact components
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      /* Autofill detection */
      @keyframes onAutoFillStart { from {/**/} to {/**/} }
      input:-webkit-autofill { animation-name: onAutoFillStart; }

      /* PrimeReact Dropdown Styling */
      .p-dropdown {
        box-shadow: none !important;
      }
      .p-dropdown:not(.p-disabled):hover {
        border-color: #d1d5db !important;
      }
      .p-dropdown:not(.p-disabled).p-focus {
        outline: 0 none !important;
        outline-offset: 0 !important;
        box-shadow: 0 0 0 2px #2a4afd !important;
        border-color: #2a4afd !important;
      }
      .p-dropdown-panel {
        border-radius: 0.5rem !important;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
      }
      .p-dropdown-panel.prime-dropdown-panel-no-scroll .p-dropdown-items-wrapper {
        max-height: none !important;
      }
      .p-dropdown-item {
        padding: 0.75rem 1rem !important;
        border-radius: 0.375rem !important;
        margin: 0.125rem !important;
      }
      .p-dropdown-item:not(.p-highlight):not(.p-disabled):hover {
        background: #e5e9ff !important;
        color: #1e3ad6 !important;
      }
      .p-dropdown-item.p-highlight {
        background: #2a4afd !important;
        color: #ffffff !important;
      }

      /* PrimeReact MultiSelect Styling */
      .p-multiselect {
        box-shadow: none !important;
      }
      .p-multiselect:not(.p-disabled):hover {
        border-color: #d1d5db !important;
      }
      .p-multiselect:not(.p-disabled).p-focus {
        outline: 0 none !important;
        outline-offset: 0 !important;
        box-shadow: 0 0 0 2px #2a4afd !important;
        border-color: #2a4afd !important;
      }
      .p-multiselect-panel {
        border-radius: 0.5rem !important;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
      }
      .p-multiselect-item {
        padding: 0.75rem 1rem !important;
        border-radius: 0.375rem !important;
        margin: 0.125rem !important;
      }
      .p-multiselect-item:not(.p-highlight):not(.p-disabled):hover {
        background: #e5e9ff !important;
        color: #1e3ad6 !important;
      }
      .p-multiselect-item.p-highlight {
        background: #2a4afd !important;
        color: #ffffff !important;
      }
      .p-multiselect-token {
        background: #2a4afd !important;
        color: #ffffff !important;
        padding: 0.25rem 0.5rem !important;
        border-radius: 0.375rem !important;
      }
      .p-multiselect-token-icon {
        color: #ffffff !important;
      }

      /* Filter input styling */
      .p-dropdown-filter,
      .p-multiselect-filter {
        padding: 0.75rem 1rem !important;
        border-radius: 0.375rem !important;
        border: 1px solid #d1d5db !important;
      }
      .p-dropdown-filter:focus,
      .p-multiselect-filter:focus {
        outline: 0 none !important;
        outline-offset: 0 !important;
        box-shadow: 0 0 0 2px #2a4afd !important;
        border-color: #2a4afd !important;
      }

      /* Elegant hover-only scrollbar for form steps */
      .progressive-lead-form-modal .flex-1.overflow-y-auto::-webkit-scrollbar,
      .mortgage-marketing-demo-form .flex-1.overflow-y-auto::-webkit-scrollbar {
        width: 6px;
      }

      .progressive-lead-form-modal .flex-1.overflow-y-auto::-webkit-scrollbar-track,
      .mortgage-marketing-demo-form .flex-1.overflow-y-auto::-webkit-scrollbar-track {
        background: transparent;
      }

      .progressive-lead-form-modal .flex-1.overflow-y-auto::-webkit-scrollbar-thumb,
      .mortgage-marketing-demo-form .flex-1.overflow-y-auto::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0);
        border-radius: 3px;
        transition: background 0.3s ease;
      }

      .progressive-lead-form-modal .flex-1.overflow-y-auto:hover::-webkit-scrollbar-thumb,
      .mortgage-marketing-demo-form .flex-1.overflow-y-auto:hover::-webkit-scrollbar-thumb {
        background: rgba(41, 75, 253, 0.15);
      }

      .progressive-lead-form-modal .flex-1.overflow-y-auto::-webkit-scrollbar-thumb:active,
      .mortgage-marketing-demo-form .flex-1.overflow-y-auto::-webkit-scrollbar-thumb:active {
        background: rgba(41, 75, 253, 0.3);
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Presentation only: the game layer derives everything from the visitor's OWN answers; nothing here feeds into the form.
  const gmFlow = effectiveSteps.filter(s => s.id !== 'welcome' && s.id !== 'thankyou' && s.id !== 'preparing-contact' && s.id !== 'submitting');
  const gmDisplayStep = currentStepId === 'nmls-retry' ? effectiveSteps.findIndex((s: any) => s.id === 'contact') - 1 : currentStep;
  const gmDone = gmFlow.filter(s => effectiveSteps.findIndex(x => x.id === s.id) <= gmDisplayStep).length;
  const gmState: GMState = {
    stepId: currentStepId,
    licensed: hasNMLSLicense,
    progress: currentStepId === 'thankyou' || currentStepId === 'submitting' ? 1 : gmFlow.length ? Math.min(1, gmDone / gmFlow.length) : 0,
    questionsLeft: gmFlow.filter(s => effectiveSteps.findIndex(x => x.id === s.id) >= gmDisplayStep).length,
    a: {
      role: formData.role || undefined, volume: formData.currentVolume || undefined, team: formData.teamSize || undefined, years: formData.yearsInIndustry || undefined,
      budget: formData.marketingBudget || undefined, loanSize: formData.avgLoanSize || undefined, situation: formData.leadGenExperience || undefined,
      sources: formData.currentLeadSources.length ? formData.currentLeadSources : undefined, agency: formData.agencyExperience || undefined,
      crm: formData.crmSystem || undefined, website: formData.websiteStatus || undefined, performance: formData.websitePerformance || undefined,
      siteUrl: formData.websiteUrl || undefined, agents: formData.agentPartnerships || undefined, importance: formData.agentPartnershipImportance || undefined,
      challenge: formData.biggestChallenge || undefined, approach: formData.systemPreference || formData.leadGenApproach || undefined,
      timeline: formData.timeline || undefined, primaryState: formData.primaryState || undefined, extraStates: formData.additionalStates,
      nl: { identity: nonLicensedIdentity || undefined, intent: nonLicensedIntent || undefined, timeline: nonLicensedTimeline || undefined, challenge: nonLicensedChallenge || undefined },
    },
    // the name shows as soon as the lookup lands (it is already on screen in the reveal card); 'verified' (badge + XP) waits for the visitor's confirmation
    profile: enrichedData && enrichedData.fullName ? { name: enrichedData.fullName, company: enrichedData.company, verified: !!verifiedFields } : null,
    contact: { valid: [fieldValidation.firstName.isValid, fieldValidation.lastName.isValid, fieldValidation.email.isValid, fieldValidation.phone.isValid].filter(Boolean).length, total: 4 },
    firstName: formData.firstName,
    submitting: currentStepId === 'submitting',
    draftId,
    nmls: formData.nmls,
  };

  return (
    <GMGame s={gmState}>
      <motion.div
        initial={false} // no JS-gated fade on first load (the card sat invisible over the sun until hydration); gm.css runs the entrance
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="mortgage-marketing-demo-form gm-card"
      >
              {/* Header with progress */}
              <div className="gm-topbar" style={currentStepId === 'thankyou' ? { paddingTop: '0.6rem', paddingBottom: '0.6rem' } : undefined}>
                {currentStepId === 'thankyou' ? (
                  // Thank you page: greeting only (no close button)
                  <div className="mb-2">
                    <div className="text-left space-y-0.5">
                      <h3 className="gm-q" style={{ fontSize: '1.2rem' }}>
                        🎉 Hey, {formatFirstName(formData.firstName)}! One more step.
                      </h3>
                      <p className="gm-sub" style={{ marginTop: '0.15rem', fontSize: '0.9rem' }}>
                        Let's get your strategy session on the calendar
                      </p>
                    </div>
                  </div>
                ) : (
                  // Other pages: eyebrow text only (no close button)
                  <div>
                    <div className="gm-eyebrow">
                      {(currentStepId === 'contact' || currentStepId === 'preparing-contact' || currentStepId === 'submitting')
                        ? (source === 'pricing' ? 'Almost done • Your custom pricing is next' : 'Almost done • Book your strategy session next')
                        : currentStepId === 'welcome' ? 'Get your custom blueprint' : 'Takes 60 seconds • Get your custom blueprint'}
                    </div>
                  </div>
                )}

                {/* Progress bar - hide on welcome and thank you */}
                {currentStepId !== 'welcome' && currentStepId !== 'thankyou' && currentStepId !== 'preparing-contact' && currentStepId !== 'submitting' && (
                  <div className="gm-track">
                    {effectiveSteps.filter(s => s.id !== 'welcome' && s.id !== 'thankyou' && s.id !== 'preparing-contact' && s.id !== 'submitting').map((step, index) => {
                      // Special handling for nmls-retry: show progress as if they're at contact step
                      // This prevents retry from looking like "starting over" when they're almost done
                      const displayStep = currentStepId === 'nmls-retry'
                        ? effectiveSteps.findIndex((s: any) => s.id === 'contact') - 1
                        : currentStep;
                      const stepIndex = effectiveSteps.findIndex(s => s.id === step.id);

                      return (
                        <div
                          key={step.id}
                          className={`gm-seg${stepIndex <= displayStep ? ' on' : ''}${stepIndex === displayStep ? ' head' : ''}`}
                        />
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-hidden flex flex-col">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 44, scale: 0.98 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: -44, scale: 0.98 }}
                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                    className="flex-1 overflow-hidden flex flex-col"
                  >
                    {/* Welcome Screen */}
                    {currentStepId === 'welcome' && (
                      <div className="gm-hero">
                        <div className="space-y-4">
                          <h1>
                            {welcome.accent && welcome.title.includes(welcome.accent) ? (
                              <>{welcome.title.split(welcome.accent)[0]}<span className="gm-grad">{welcome.accent}</span>{welcome.title.split(welcome.accent)[1]}</>
                            ) : welcome.title}
                          </h1>
                          <p className="gm-lede">
                            {welcome.subtitle}
                          </p>
                        </div>

                        <div className="w-full max-w-md flex flex-col items-center">
                          <ButtonCta
                            variant="primary"
                            showArrow={true}
                            onClick={handleNext}
                            className="text-center justify-center w-full"
                          >
                            {welcome.buttonText}
                          </ButtonCta>
                          <p className="text-sm text-gray-600 mt-4 font-medium">
                            Free strategy session • No courses or coaching pitches
                          </p>
                        </div>

                        <GMJourney />

                        {/* Premium Google Reviews Badge */}
                        <div className="mt-4">
                          <div className="gm-review">
                            {/* Google Logo */}
                            <svg width="20" height="20" viewBox="0 0 24 24" className="flex-shrink-0">
                              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>

                            {/* Rating */}
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold text-gray-900">4.9</span>

                              {/* Stars */}
                              <div className="flex gap-0.5">
                                {[...Array(5)].map((_, i) => (
                                  <svg
                                    key={i}
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="#FFC107"
                                    className="drop-shadow-sm"
                                  >
                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                  </svg>
                                ))}
                              </div>

                              {/* Review Count */}
                              <span className="text-sm text-gray-600 font-medium">
                                750+ Google Reviews
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Trust Indicators */}
                        <div className="mt-2 pt-6 border-t border-gray-200 w-full max-w-lg">
                          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-emerald-500">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                              </svg>
                              <span>15+ Years in Business</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-emerald-500">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                              </svg>
                              <span>5,247+ Active Users</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-emerald-500">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                              </svg>
                              <span>3.2M+ Leads Generated</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Phase 2: NMLS Pre-Qualification (Task 2.1) */}
                    {currentStepId === 'nmls-prequalification' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            Do you currently hold an active NMLS license?
                          </h3>
                          <p className="gm-sub">
                            This helps us customize your experience and unlock exclusive features
                          </p>
                        </div>
                        <div className="gm-body">
                          <div className="space-y-4 pt-2">
                            {/* Yes Option */}
                            <button
                              onClick={() => {
                                setHasNMLSLicense(true);
                                // Auto-advance to NMLS input step
                                setTimeout(() => handleNext(), 200);
                              }}
                              data-dm-option className={`gm-opt big ${hasNMLSLicense === true ? 'is-on' : ''}`}
                            >
                              <div className="flex items-center gap-4" style={{ width: '100%' }}>
                                <div className="gm-dot">
                                  {hasNMLSLicense === true && (
                                    <Check className="w-4 h-4 text-white" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className="gm-opt-title">
                                    Yes, I have an NMLS license
                                  </p>
                                  <p className="gm-opt-desc">
                                    We'll pull your production data to create a custom growth plan
                                  </p>
                                </div>
                              </div>
                            </button>

                            {/* No Option */}
                            <button
                              onClick={() => {
                                setHasNMLSLicense(false);

                                // Generate draftId for non-licensed leads (same as licensed leads)
                                if (!draftId) {
                                  const newDraftId = `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                                  setDraftId(newDraftId);
                                  console.log('📝 Non-Licensed Lead: Generated draftId:', newDraftId);
                                }

                                setCurrentStep(0); // Reset to first step immediately

                                // Update URL after React processes state changes
                                setTimeout(() => {
                                  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : (searchParams?.toString() || ''));
                                  params.set('step', 'who-are-you');
                                  router.push(`${FORM_BASE}?${params.toString()}`, { scroll: false });
                                }, 100);
                              }}
                              data-dm-option className={`gm-opt big ${hasNMLSLicense === false ? 'is-on' : ''}`}
                            >
                              <div className="flex items-center gap-4" style={{ width: '100%' }}>
                                <div className="gm-dot">
                                  {hasNMLSLicense === false && (
                                    <Check className="w-4 h-4 text-white" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className="gm-opt-title">
                                    No, I don't have an NMLS license
                                  </p>
                                  <p className="gm-opt-desc">
                                    No problem! We'll still create a personalized strategy for you
                                  </p>
                                </div>
                              </div>
                            </button>

                            {/* Helper Text */}
                            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                              <p className="text-sm text-gray-700 flex items-start gap-2">
                                <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                                <span>
                                  If you have an NMLS license, we can pull your real production data and show you exactly how much revenue you could generate
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Step 0: Calculator Check */}
                    {currentStepId === 'calculator-check' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            {showAdjustInputs && adjustmentReason === 'new'
                              ? "Let's set your baseline numbers"
                              : "Let's confirm your numbers"}
                          </h3>
                          <p className="gm-sub">
                            {showAdjustInputs && adjustmentReason === 'new'
                              ? "We'll use these for your custom plan:"
                              : "Your current calculator settings:"}
                          </p>
                        </div>
                        <div className="gm-body">
                          <AnimatePresence mode="wait">
                            {!showAdjustInputs && (
                              <motion.div
                                key="check-view"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                              >
                                <div className="space-y-4">
                                  <div className="bg-[#ff34a7]/5 border border-[#ff34a7] rounded-lg p-4 space-y-2">
                                    <div className="flex justify-between">
                                      <span className="text-gray-700">Estimated monthly ad spend:</span>
                                      <span className="font-semibold text-gray-900">
                                        ${adjustedValues?.adSpend?.toLocaleString()}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-700">Average commission per loan:</span>
                                      <span className="font-semibold text-gray-900">
                                        ${adjustedValues?.commission?.toLocaleString()}
                                      </span>
                                    </div>
                                    {/* HIDDEN: Lead Contact Rate - Users don't typically know this metric
                                    <div className="flex justify-between">
                                      <span className="text-gray-700">Lead contact rate:</span>
                                      <span className="font-semibold text-gray-900">
                                        {adjustedValues?.contactRate}%
                                      </span>
                                    </div>
                                    */}
                                  </div>

                                  <div className="space-y-3">
                                    <button
                                      onClick={() => handleAutoAdvance({
                                        calculatorConfirmed: true,
                                        calculatorValues: adjustedValues
                                      })}
                                      className="w-full px-6 py-4 text-left rounded-lg border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all"
                                    >
                                      <span className="font-medium text-gray-900">
                                        Yes, these are accurate
                                      </span>
                                    </button>
                                    <button
                                      onClick={() => {
                                        setAdjustmentReason('modify');
                                        setShowAdjustInputs(true);
                                      }}
                                      className="w-full px-6 py-4 text-left rounded-lg border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all"
                                    >
                                      <span className="font-medium text-gray-900">
                                        Let me adjust these
                                      </span>
                                    </button>
                                    <button
                                      onClick={() => {
                                        setAdjustmentReason('new');
                                        setShowAdjustInputs(true);
                                      }}
                                      className="w-full px-6 py-4 text-left rounded-lg border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all"
                                    >
                                      <span className="font-medium text-gray-900">
                                        I haven't used the calculator yet
                                      </span>
                                    </button>
                                  </div>
                                </div>
                                <SecurityBadge />
                              </motion.div>
                            )}

                            {showAdjustInputs && (
                              <motion.div
                                key="adjust-view"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                                className="space-y-6"
                              >
                              <p className="text-sm text-gray-600">
                                {adjustmentReason === 'modify'
                                  ? 'Adjust your numbers to match your specific situation:'
                                  : 'Set your baseline numbers for a custom plan:'}
                              </p>

                              <CustomSlider
                                field={{
                                  ...defaultFieldConfigs.adSpend,
                                  label: 'Estimated monthly ad spend'
                                } as any}
                                value={adjustedValues.adSpend}
                                onChange={(value) => setAdjustedValues(prev => ({
                                  ...prev,
                                  adSpend: value
                                }))}
                              />

                              <CustomSlider
                                field={{
                                  ...defaultFieldConfigs.avgCommission,
                                  label: 'Average commission per loan'
                                } as any}
                                value={adjustedValues.commission}
                                onChange={(value) => setAdjustedValues(prev => ({
                                  ...prev,
                                  commission: value
                                }))}
                              />

                              {/* HIDDEN: Lead Contact Rate Slider - Users don't typically know this metric
                              <CustomSlider
                                field={defaultFieldConfigs.leadContactRate}
                                value={adjustedValues.contactRate}
                                onChange={(value) => setAdjustedValues(prev => ({
                                  ...prev,
                                  contactRate: value
                                }))}
                              />
                              */}

                              <button
                                onClick={() => handleAutoAdvance({
                                  calculatorConfirmed: true,
                                  calculatorValues: { ...adjustedValues, ...adjustedValues }
                                })}
                                style={{ backgroundColor: '#ff34a7' }}
                                className="w-full px-6 py-3 hover:opacity-90 text-white font-medium rounded-lg transition-all"
                              >
                                Continue with these values
                              </button>
                              <SecurityBadge />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </>
                    )}

                    {/* Phase 2: NMLS Step (Task B1 - Rebuilt) */}
                    {currentStepId === 'nmls-early' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            First, let's verify you're a licensed mortgage professional
                          </h3>
                          <p className="gm-sub">
                            Enter your NMLS # to unlock your custom production analysis
                          </p>
                        </div>
                        <div className="gm-body">
                          <div className="space-y-4 pt-2">
                            <div className="relative">
                              <label className="gm-label">
                                NMLS #
                              </label>

                              <input
                                ref={nmlsInputRef}
                                type="text"
                                inputMode="numeric"
                                autoFocus
                                value={formData.nmls}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '');
                                  if (value.length <= 7) {
                                    if (value.length > formData.nmls.length) sfx.digits(value.slice(formData.nmls.length), formData.nmls.length);
                                    else if (value.length < formData.nmls.length) sfx.erase(value.length);
                                    setFormData(prev => ({ ...prev, nmls: value }));
                                    setNmlsValidationError(null); // Clear error on edit
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && formData.nmls.length >= 4 && !nmlsValidating) {
                                    handleNMLSValidation();
                                  }
                                }}
                                className="gm-input gm-nmls"
                                placeholder="Enter your NMLS number"
                              />

                              {/* Error Message */}
                              {nmlsValidationError && (
                                <motion.div
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
                                >
                                  <p className="text-yellow-800 font-medium flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4" />
                                    {nmlsValidationError}
                                  </p>
                                  <div className="mt-2 flex gap-3 text-sm">
                                    <button
                                      onClick={() => handleAutoAdvance({ nmls: formData.nmls })}
                                      className="text-yellow-600 hover:text-yellow-700 underline"
                                    >
                                      Continue anyway
                                    </button>
                                  </div>
                                </motion.div>
                              )}

                              {/* Helper Text */}
                              {!nmlsValidationError && (
                                <p className="text-xs text-gray-500 mt-2">
                                  We'll pull your real production data to show your exact growth potential
                                </p>
                              )}
                            </div>

                            {/* Continue Button */}
                            <button
                              onClick={handleNMLSValidation}
                              disabled={formData.nmls.length < 4 || nmlsValidating}
                              className="gm-btn gm-btn-primary w-full"
                            >
                              {nmlsValidating ? (
                                <span className="flex items-center justify-center gap-2">
                                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  Validating...
                                </span>
                              ) : (
                                'Continue'
                              )}
                            </button>
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* NMLS Retry Step */}
                    {currentStepId === 'nmls-retry' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            Let's try again with your correct NMLS
                          </h3>
                          <p className="gm-sub">
                            Enter the correct NMLS number to continue
                          </p>
                        </div>
                        <div className="gm-body">
                          <div className="space-y-4 pt-2">
                            <div className="relative">
                              <label className="gm-label">
                                NMLS #
                              </label>

                              <input
                                ref={nmlsInputRef}
                                type="text"
                                inputMode="numeric"
                                autoFocus
                                value={formData.nmls}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '');
                                  if (value.length <= 7) {
                                    if (value.length > formData.nmls.length) sfx.digits(value.slice(formData.nmls.length), formData.nmls.length);
                                    else if (value.length < formData.nmls.length) sfx.erase(value.length);
                                    setFormData(prev => ({ ...prev, nmls: value }));
                                    setNmlsValidationError(null); // Clear error on edit
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && formData.nmls.length >= 4 && !nmlsValidating) {
                                    handleNMLSValidation();
                                  }
                                }}
                                className="gm-input gm-nmls"
                                placeholder="Enter your NMLS number"
                              />

                              {/* Error Message */}
                              {nmlsValidationError && (
                                <motion.div
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
                                >
                                  <p className="text-yellow-800 font-medium flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4" />
                                    {nmlsValidationError}
                                  </p>
                                  <div className="mt-2 flex gap-3 text-sm">
                                    <button
                                      onClick={() => handleAutoAdvance({ nmls: formData.nmls })}
                                      className="text-yellow-600 hover:text-yellow-700 underline"
                                    >
                                      Continue anyway
                                    </button>
                                  </div>
                                </motion.div>
                              )}

                              {/* Helper Text */}
                              {!nmlsValidationError && (
                                <p className="text-xs text-gray-500 mt-2">
                                  We'll pull your real production data to show your exact growth potential
                                </p>
                              )}
                            </div>

                            {/* Continue Button */}
                            <button
                              onClick={handleNMLSValidation}
                              disabled={formData.nmls.length < 4 || nmlsValidating}
                              className="gm-btn gm-btn-primary w-full"
                            >
                              {nmlsValidating ? (
                                <span className="flex items-center justify-center gap-2">
                                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  Validating...
                                </span>
                              ) : (
                                'Continue'
                              )}
                            </button>

                            {/* I Don't Remember Link */}
                            <div className="mt-4 text-center">
                              <button
                                type="button"
                                onClick={() => {
                                  console.log('User clicked: I don\'t remember my NMLS');

                                  // Clear NMLS data
                                  setFormData(prev => ({ ...prev, nmls: '' }));
                                  setEnrichedData(null);
                                  setEnriched(false);
                                  setMatchStatus(null);

                                  // Hide retry step, skip verification
                                  setShouldShowNMLSRetry(false);
                                  setSkipVerification(true);

                                  // Trigger navigation via useEffect
                                  setShouldNavigateToContact(true);
                                }}
                                className="gm-link"
                                style={{ fontSize: '17px' }}
                              >
                                I don't remember my NMLS number
                              </button>
                            </div>
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* NMLS Loading - 4 second intermediary screen */}
                    {currentStepId === 'nmls-loading' && (
                      <>
                        <div className="gm-head text-center">
                          <div className="gm-radar" aria-hidden="true"><i /><i /><i /><b /><em /></div>
                          <h3 className="gm-q mb-2">
                            Building Your Custom Plan
                          </h3>
                          <p className="gm-sub">
                            Next, we'll ask a few quick questions to understand your business and create your personalized marketing strategy...
                          </p>
                        </div>
                      </>
                    )}

                    {/* NMLS Confirmation Container - Always visible on this step */}
                    {currentStepId === 'nmls-confirm' && (
                      <div className="flex-1 flex flex-col overflow-hidden">

                        {/* Loading State - While waiting for data */}
                        {!enrichedData && matchStatus !== 'not_found' && matchStatus !== 'timeout' && (
                          <div className="flex-1 flex items-center justify-center px-6">
                            <div className="text-center">
                              <div className="gm-radar" aria-hidden="true"><i /><i /><i /><b /><em /></div>
                              <h3 className="gm-q mb-2">
                                Loading your profile...
                              </h3>
                              <p className="gm-sub">
                                We're retrieving your information
                              </p>
                            </div>

                          </div>
                        )}

                        {/* Timeout Screen - the lookup took too long; the Continue button in the footer moves on to the contact step */}
                        {matchStatus === 'timeout' && !enrichedData && (
                          <div className="flex-1 flex items-center justify-center px-6">
                            <div className="text-center max-w-md" role="status">
                              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-100 flex items-center justify-center">
                                <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                              </div>
                              <h3 className="gm-q mb-2">
                                We couldn't load your profile
                              </h3>
                              <p className="gm-sub">
                                It took too long. Keep going and type in your details yourself.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* No Match Screen - Centered */}
                        {matchStatus === 'not_found' && (
                          <div className="flex-1 flex items-center justify-center px-6">
                            <div className="text-center max-w-md">
                              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-100 flex items-center justify-center">
                                <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                              </div>
                              <h3 className="gm-q mb-2">
                                We couldn't find your profile
                              </h3>
                              <p className="gm-sub">
                                We weren't able to locate a profile for NMLS #{formData.nmls}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Lock-on (REVEAL_LOCK_MS): a lime check badge pops with a ring burst, then the profile card enters. CSS only (transform and opacity). */}
                        {showVerificationSuccess && (
                          <div key="success" role="status" className="gm-lock flex-1 flex items-start justify-center pt-24 px-6">
                            <div className="text-center">
                              <div className="gm-lock-badge">
                                <span className="gm-lock-ring" aria-hidden="true" />
                                <span className="gm-lock-ring r2" aria-hidden="true" />
                                <svg className="w-11 h-11" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                              <h2 className="gm-q gm-lock-h">
                                Success! We found your <span className="gm-grad">profile!</span>
                              </h2>
                            </div>
                          </div>
                        )}

                        {/* Phase 2: Step 2b - NMLS Confirmation - Data Loaded */}
                        <AnimatePresence mode="wait">
                          {enrichedData && !showVerificationSuccess && (
                        <motion.div
                          key="verification"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: revealReduced() ? 0.4 : 0.15, ease: "easeOut" }}
                          className="gm-rv flex flex-col h-full"
                        >

                        {/* FIXED HEADER - Avatar + Name */}
                        <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-200">
                          <div className="flex items-center space-x-4">
                            <div className="gm-rv-av flex-shrink-0">
                            <AvatarCircle
                              fullName={enrichedData.fullName || 'Unknown'}
                              size="lg"
                              className="flex-shrink-0"
                            />
                            </div>
                            <div className="gm-rv-name">
                              <div className="flex items-center gap-2">
                                <h2 className="text-xl font-bold text-gray-900">{enrichedData.fullName}</h2>
                              </div>
                              <p className="text-sm text-gray-600">NMLS #{formData.nmls}</p>
                            </div>
                          </div>
                          <p className="text-center text-sm text-gray-600 mt-4 px-6">
                            Uncheck any information that isn't correct
                          </p>
                        </div>

                        {/* SCROLLABLE MIDDLE - Checkboxes */}
                        <div className="flex-1 overflow-y-auto px-6 py-6 pb-8">
                          <div className="space-y-6 pt-2">

                            {/* Primary Information Container */}
                            <div className="mb-6">
                              <h3 className="gm-panel-title">Primary Information</h3>
                              <div className="gm-panel">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                  {/* Name */}
                                  <label className="gm-fact">
                                <input
                                  type="checkbox"
                                  checked={verifiedCheckboxes.fullName}
                                  onChange={(e) => setVerifiedCheckboxes(prev => ({ ...prev, fullName: e.target.checked }))}
                                  className="gm-check"
                                />
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-gray-700">Name:</span>
                                  <span className="text-sm text-gray-900 ml-2">{enrichedData.fullName}</span>
                                </div>
                              </label>

                              {/* NMLS */}
                              <label className="gm-fact">
                                <input
                                  type="checkbox"
                                  checked={verifiedCheckboxes.nmlsNumber}
                                  onChange={(e) => setVerifiedCheckboxes(prev => ({ ...prev, nmlsNumber: e.target.checked }))}
                                  className="gm-check"
                                />
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-gray-700">NMLS:</span>
                                  <span className="text-sm text-gray-900 ml-2">{formData.nmls}</span>
                                </div>
                              </label>

                              {/* Company */}
                              {enrichedData.company && (
                                <label className="gm-fact">
                                  <input
                                    type="checkbox"
                                    checked={verifiedCheckboxes.company}
                                    onChange={(e) => setVerifiedCheckboxes(prev => ({ ...prev, company: e.target.checked }))}
                                    className="gm-check"
                                  />
                                  <div className="flex-1">
                                    <span className="text-sm font-medium text-gray-700">Company:</span>
                                    <span className="text-sm text-gray-900 ml-2">{enrichedData.company}</span>
                                  </div>
                                </label>
                              )}

                              {/* Title */}
                              {enrichedData.title && (
                                <label className="gm-fact">
                                  <input
                                    type="checkbox"
                                    checked={verifiedCheckboxes.title}
                                    onChange={(e) => setVerifiedCheckboxes(prev => ({ ...prev, title: e.target.checked }))}
                                    className="gm-check"
                                  />
                                  <div className="flex-1">
                                    <span className="text-sm font-medium text-gray-700">Title:</span>
                                    <span className="text-sm text-gray-900 ml-2">{enrichedData.title}</span>
                                  </div>
                                </label>
                              )}

                                  {/* Location */}
                                  {(enrichedData.city || enrichedData.primaryState) && (
                                    <label className="gm-fact">
                                      <input
                                        type="checkbox"
                                        checked={verifiedCheckboxes.location}
                                        onChange={(e) => setVerifiedCheckboxes(prev => ({ ...prev, location: e.target.checked }))}
                                        className="gm-check"
                                      />
                                      <div className="flex-1">
                                        <span className="text-sm font-medium text-gray-700">Location:</span>
                                        <span className="text-sm text-gray-900 ml-2">{enrichedData.city}, {enrichedData.primaryState}</span>
                                      </div>
                                    </label>
                                  )}

                                  {/* Years Licensed */}
                                  {enrichedData.yearsLicensed && (
                                    <label className="gm-fact">
                                      <input
                                        type="checkbox"
                                        checked={verifiedCheckboxes.yearsLicensed}
                                        onChange={(e) => setVerifiedCheckboxes(prev => ({ ...prev, yearsLicensed: e.target.checked }))}
                                        className="gm-check"
                                      />
                                      <div className="flex-1">
                                        <span className="text-sm font-medium text-gray-700">Years Licensed:</span>
                                        <span className="text-sm text-gray-900 ml-2">{enrichedData.yearsLicensed}</span>
                                      </div>
                                    </label>
                                  )}

                                </div>
                              </div>
                            </div>

                            {/* Production Container */}
                            {/* Withheld or missing production (new licensee, branch manager) shows no empty box */}
                            {(enrichedData.totalLoans || enrichedData.totalVolume || enrichedData.avgLoanAmount || enrichedData.purchase || enrichedData.refinance) && (
                            <div className="gm-rv-prod mb-6">
                              <h3 className="gm-panel-title">Last 12 Months Production</h3>
                              <div className="gm-panel">
                                <GMSplit purchase={enrichedData.purchase} refinance={enrichedData.refinance} />
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                              {/* Total Loans - Featured */}
                              {enrichedData.totalLoans && (
                                <div className="md:col-span-2 gm-feature mb-2">
                                  <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={verifiedCheckboxes.totalLoans}
                                      onChange={(e) => setVerifiedCheckboxes(prev => ({ ...prev, totalLoans: e.target.checked }))}
                                      className="gm-check"
                                    />
                                    <div className="flex-1">
                                      <span className="text-sm font-medium text-gray-700">Total Loans Originated:</span>
                                      <span className="text-2xl font-bold text-gray-900 ml-2 block mt-1">{enrichedData.totalLoans}</span>
                                    </div>
                                  </label>
                                </div>
                              )}

                              {/* Total Volume */}
                              {enrichedData.totalVolume && (
                                <label className="gm-fact">
                                  <input
                                    type="checkbox"
                                    checked={verifiedCheckboxes.totalVolume}
                                    onChange={(e) => setVerifiedCheckboxes(prev => ({ ...prev, totalVolume: e.target.checked }))}
                                    className="gm-check"
                                  />
                                  <div className="flex-1">
                                    <span className="text-sm font-medium text-gray-700">Total Volume:</span>
                                    <span className="text-sm text-gray-900 ml-2">{enrichedData.totalVolume}</span>
                                  </div>
                                </label>
                              )}

                              {/* Average Loan Amount */}
                              {enrichedData.avgLoanAmount && (
                                <label className="gm-fact">
                                  <input
                                    type="checkbox"
                                    checked={verifiedCheckboxes.avgLoanAmount}
                                    onChange={(e) => setVerifiedCheckboxes(prev => ({ ...prev, avgLoanAmount: e.target.checked }))}
                                    className="gm-check"
                                  />
                                  <div className="flex-1">
                                    <span className="text-sm font-medium text-gray-700">Average Loan Amount:</span>
                                    <span className="text-sm text-gray-900 ml-2">{enrichedData.avgLoanAmount}</span>
                                  </div>
                                </label>
                              )}

                              {/* Purchase % */}
                              {enrichedData.purchase && (
                                <label className="gm-fact">
                                  <input
                                    type="checkbox"
                                    checked={verifiedCheckboxes.purchase}
                                    onChange={(e) => setVerifiedCheckboxes(prev => ({ ...prev, purchase: e.target.checked }))}
                                    className="gm-check"
                                  />
                                  <div className="flex-1">
                                    <span className="text-sm font-medium text-gray-700">Purchase:</span>
                                    <span className="text-sm text-gray-900 ml-2">{enrichedData.purchase}</span>
                                  </div>
                                </label>
                              )}

                              {/* Refinance % */}
                              {enrichedData.refinance && (
                                <label className="gm-fact">
                                  <input
                                    type="checkbox"
                                    checked={verifiedCheckboxes.refinance}
                                    onChange={(e) => setVerifiedCheckboxes(prev => ({ ...prev, refinance: e.target.checked }))}
                                    className="gm-check"
                                  />
                                  <div className="flex-1">
                                    <span className="text-sm font-medium text-gray-700">Refinance:</span>
                                    <span className="text-sm text-gray-900 ml-2">{enrichedData.refinance}</span>
                                  </div>
                                </label>
                              )}

                                </div>
                              </div>
                            </div>
                            )}

                          </div>
                          <SecurityBadge />
                        </div>

                        </motion.div>
                          )}
                        </AnimatePresence>

                      </div>
                    )}

                    {/* Step 1: Role */}
                    {currentStepId === 'role' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            What's your role?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              'Loan Officer',
                              'Branch Manager',
                              'Mortgage Broker/Owner',
                              'Marketing Leader',
                              'Operations Executive',
                              'Other',
                            ].map((option) => (
                              <button
                                key={option}
                                onClick={() => handleAutoAdvance({ role: option })}
                                data-dm-option className={`gm-opt ${formData.role === option ? 'is-on' : ''}`}
                              >
                                <span className="gm-opt-label">{option}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 2: Current Volume */}
                    {currentStepId === 'volume' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            How many loans are you personally closing per month?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              '0-1 loans',
                              '2-3 loans',
                              '4-5 loans',
                              '6-10 loans',
                              '11-20 loans',
                              '21-50 loans',
                              '50+ loans',
                            ].map((option) => (
                              <button
                                key={option}
                                onClick={() => handleAutoAdvance({ currentVolume: option })}
                                data-dm-option className={`gm-opt ${formData.currentVolume === option ? 'is-on' : ''}`}
                              >
                                <span className="gm-opt-label">{option}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 3: Team Size */}
                    {currentStepId === 'team' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            How many people are on your loan production team (including yourself)?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              'Just me',
                              '2-3 people',
                              '4-6 people',
                              '7-10 people',
                              '11-20 people',
                              '20+ people',
                            ].map((option) => (
                              <button
                                key={option}
                                onClick={() => handleAutoAdvance({ teamSize: option })}
                                data-dm-option className={`gm-opt ${formData.teamSize === option ? 'is-on' : ''}`}
                              >
                                <span className="gm-opt-label">{option}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 4: Years in Industry */}
                    {currentStepId === 'years-in-industry' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            How many years have you been in the mortgage industry?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              { value: 'Less than 1 year', label: 'Less than 1 year', description: 'Just getting started' },
                              { value: '1-3 years', label: '1-3 years', description: 'Building momentum' },
                              { value: '4-7 years', label: '4-7 years', description: 'Established professional' },
                              { value: '8-15 years', label: '8-15 years', description: 'Seasoned veteran' },
                              { value: '15+ years', label: '15+ years', description: 'Industry leader' },
                            ].map((option) => (
                              <button
                                key={option.value}
                                onClick={() => handleAutoAdvance({ yearsInIndustry: option.value })}
                                data-dm-option className={`gm-opt ${formData.yearsInIndustry === option.value ? 'is-on' : ''}`}
                              >
                                <div className="flex flex-col">
                                  <span className="gm-opt-label">{option.label}</span>
                                  <span className="text-sm text-gray-500 mt-1">{option.description}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 5: Marketing Budget */}
                    {currentStepId === 'budget' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            What's your total monthly marketing/lead budget?
                          </h3>
                          <p className="gm-sub">
                            Include all lead purchases, advertising, marketing tools
                          </p>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              '$100 or less',
                              '$101 - $250',
                              '$251 - $500',
                              '$501 - $1,000',
                              '$1,001 - $2,000',
                              '$2,001 - $3,000',
                              '$3,001 - $5,000',
                              '$5,001 - $10,000',
                              '$10,001 - $20,000',
                              '$20,001 - $30,000',
                              '$30,001 - $50,000',
                              'Over $50,000',
                            ].map((option) => (
                              <button
                                key={option}
                                onClick={() => handleAutoAdvance({ marketingBudget: option })}
                                data-dm-option className={`gm-opt ${formData.marketingBudget === option ? 'is-on' : ''}`}
                              >
                                <span className="gm-opt-label">{option}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 5: Average Loan Size */}
                    {currentStepId === 'loan-size' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            What's your average loan size?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              'Under $150k',
                              '$150k - $250k',
                              '$250k - $400k',
                              '$400k - $600k',
                              '$600k - $800k',
                              '$800k - $1M',
                              'Over $1M',
                            ].map((option) => (
                              <button
                                key={option}
                                onClick={() => handleAutoAdvance({ avgLoanSize: option })}
                                data-dm-option className={`gm-opt ${formData.avgLoanSize === option ? 'is-on' : ''}`}
                              >
                                <span className="gm-opt-label">{option}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 6: Lead Gen Experience */}
                    {currentStepId === 'experience' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            What's your current lead generation situation?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              '100% referral/sphere based',
                              'Buying shared/recycled leads',
                              'Running my own Google/Facebook ads',
                              'Have team/assistant running campaigns',
                              'Mix of paid and referral',
                            ].map((option) => (
                              <button
                                key={option}
                                onClick={() => handleAutoAdvance({ leadGenExperience: option })}
                                data-dm-option className={`gm-opt ${formData.leadGenExperience === option ? 'is-on' : ''}`}
                              >
                                <span className="gm-opt-label">{option}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 7: Current Lead Sources (Multi-select) */}
                    {currentStepId === 'sources' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            Which lead sources are you currently using?
                          </h3>
                          <p className="gm-sub">Check all that apply</p>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              { id: 'zillow', label: 'Zillow/Realtor.com' },
                              { id: 'facebook', label: 'Facebook ads (self-managed)' },
                              { id: 'google', label: 'Google ads (self-managed)' },
                              { id: 'agency', label: 'Agency-managed campaigns' },
                              { id: 'purchased', label: 'Purchased leads (LendingTree, etc.)' },
                              { id: 'other', label: 'Another lead source' },
                              { id: 'none', label: 'None currently' },
                            ].map((source) => (
                              <button
                                key={source.id}
                                onClick={() => handleMultiSelectToggle('currentLeadSources', source.id)}
                                data-dm-option className={`gm-opt ${formData.currentLeadSources.includes(source.id) ? 'is-on' : ''}`}
                              >
                                <div
                                  className="gm-dot sq"
                                >
                                  {formData.currentLeadSources.includes(source.id) && (
                                    <Check className="w-3 h-3 text-white" />
                                  )}
                                </div>
                                <span className="gm-opt-label">{source.label}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 8: Agency Experience */}
                    {currentStepId === 'agency' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            Have you worked with a marketing agency for lead generation before?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              'Never',
                              'Currently working with one',
                              'Yes, stopped due to poor results',
                              'Yes, stopped due to cost',
                              'Yes, it worked but outgrew it',
                            ].map((option) => (
                              <button
                                key={option}
                                onClick={() => handleAutoAdvance({ agencyExperience: option })}
                                data-dm-option className={`gm-opt ${formData.agencyExperience === option ? 'is-on' : ''}`}
                              >
                                <span className="gm-opt-label">{option}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 9: CRM System */}
                    {currentStepId === 'crm' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            Which CRM are you using?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list gm-tiles">
                            {[
                              'I don\'t use a CRM',
                              'Aidium',
                              'Bonzo',
                              'Go High Level',
                              'HubSpot',
                              'Insellerate',
                              'Jungo',
                              'Lead Mailbox',
                              'LionDesk',
                              'Relcu',
                              'Salesforce',
                              'Surefire',
                              'Total Expert',
                              'Velocify',
                              'Something else',
                            ].map((option) => (
                              <button
                                key={option}
                                onClick={() => handleAutoAdvance({ crmSystem: option })}
                                data-dm-option className={`gm-opt ${formData.crmSystem === option ? 'is-on' : ''}`}
                              >
                                <CrmMark option={option} />
                                <span className="gm-opt-label">{option}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 10: Website Status */}
                    {currentStepId === 'website-status' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            Which best describes your current website?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              'I have a website that actively generates leads',
                              'I have a website but it needs work',
                              "I use my company's website (not my own)",
                              "I don't have a website",
                            ].map((option) => (
                              <button
                                key={option}
                                onClick={() => handleAutoAdvance({ websiteStatus: option })}
                                data-dm-option className={`gm-opt ${formData.websiteStatus === option ? 'is-on' : ''}`}
                              >
                                <span className="gm-opt-label">{option}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 11: Website Performance */}
                    {currentStepId === 'website-performance' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            How's your website performing for lead generation?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              'Excellent - consistent lead flow',
                              'Decent - some leads but inconsistent',
                              'Poor - rarely generates leads',
                              'Not set up for lead capture',
                            ].map((option) => (
                              <button
                                key={option}
                                onClick={() => handleAutoAdvance({ websitePerformance: option })}
                                data-dm-option className={`gm-opt ${formData.websitePerformance === option ? 'is-on' : ''}`}
                              >
                                <span className="gm-opt-label">{option}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 12: Website URL */}
                    {currentStepId === 'website-url' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            What's your website URL?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="space-y-4 pt-2">
                            <div className="relative">
                              <input
                                ref={websiteUrlInputRef}
                                type="text"
                                autoFocus
                                value={formData.websiteUrl}
                                onChange={(e) => {
                                  const cursorPos = e.target.selectionStart;
                                  const value = e.target.value;
                                  setFormData(prev => ({ ...prev, websiteUrl: value }));
                                  const isValid = validateWebsiteUrl(value);
                                  setFieldValidation(prev => ({
                                    ...prev,
                                    websiteUrl: { ...prev.websiteUrl, isValid }
                                  }));
                                  // Restore cursor position after React re-render
                                  setTimeout(() => {
                                    if (websiteUrlInputRef.current && cursorPos !== null) {
                                      websiteUrlInputRef.current.setSelectionRange(cursorPos, cursorPos);
                                    }
                                  }, 0);
                                }}
                                onBlur={() => {
                                  setFieldValidation(prev => ({
                                    ...prev,
                                    websiteUrl: { ...prev.websiteUrl, isTouched: true }
                                  }));
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && isStepValid()) {
                                    handleNext();
                                  }
                                }}
                                className={`gm-input gm-pr ${fieldValidation.websiteUrl.isValid ? 'is-valid' : fieldValidation.websiteUrl.isTouched ? 'is-invalid' : ''}`}
                                placeholder="example.com"
                              />
                              {fieldValidation.websiteUrl.isValid && (
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.5, rotate: -90 }}
                                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                  transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
                                  className="absolute right-3 top-3 bottom-3 flex items-center"
                                  style={{ pointerEvents: 'none' }}
                                >
                                  <Check className="w-5 h-5 text-green-500" strokeWidth={3} />
                                </motion.div>
                              )}
                              {!fieldValidation.websiteUrl.isValid && fieldValidation.websiteUrl.isTouched && (
                                <motion.div
                                  initial={{ opacity: 0, x: 0 }}
                                  animate={{ opacity: 1, x: [0, -4, 4, -4, 4, 0] }}
                                  transition={{ duration: 0.4 }}
                                  className="absolute right-3 top-3 bottom-3 flex items-center"
                                  style={{ pointerEvents: 'none' }}
                                >
                                  <X className="w-5 h-5 text-red-500" strokeWidth={3} />
                                </motion.div>
                              )}
                            </div>
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 13: Agent Partnerships */}
                    {/* Task 2.3: Step 17 - Agent Partnerships (Yes/No) */}
                    {currentStepId === 'partnerships' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            Do you work with real estate agent partners?
                          </h3>
                          <p className="gm-sub">
                            We can analyze your agent network to identify your top referral sources
                          </p>
                        </div>
                        <div className="gm-body">
                          <div className="space-y-4">
                            {/* Yes Option */}
                            <button
                              onClick={() => handleAutoAdvance({ agentPartnerships: 'Yes' })}
                              data-dm-option className={`gm-opt big ${formData.agentPartnerships === 'Yes' ? 'is-on' : ''}`}
                            >
                              <div className="flex items-center gap-4" style={{ width: '100%' }}>
                                <div className="gm-dot">
                                  {formData.agentPartnerships === 'Yes' && (
                                    <Check className="w-4 h-4 text-white" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className="gm-opt-title">
                                    Yes, I work with agents
                                  </p>
                                  <p className="gm-opt-desc">
                                    Agents send me referrals or we have partnerships
                                  </p>
                                </div>
                              </div>
                            </button>

                            {/* No Option */}
                            <button
                              onClick={() => handleAutoAdvance({ agentPartnerships: 'No' })}
                              data-dm-option className={`gm-opt big ${formData.agentPartnerships === 'No' ? 'is-on' : ''}`}
                            >
                              <div className="flex items-center gap-4" style={{ width: '100%' }}>
                                <div className="gm-dot">
                                  {formData.agentPartnerships === 'No' && (
                                    <Check className="w-4 h-4 text-white" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className="gm-opt-title">
                                    No, I don't work with agents
                                  </p>
                                  <p className="gm-opt-desc">
                                    I generate leads through other channels
                                  </p>
                                </div>
                              </div>
                            </button>
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 11: Biggest Challenge */}
                    {currentStepId === 'challenge' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            What's your biggest challenge right now?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              'Don\'t know where to start',
                              'Tried ads, lost money',
                              'Leads don\'t convert',
                              'No time to follow up properly',
                              'Can\'t scale beyond referrals',
                              'Cost per lead too high',
                            ].map((option) => (
                              <button
                                key={option}
                                onClick={() => handleAutoAdvance({ biggestChallenge: option })}
                                data-dm-option className={`gm-opt ${formData.biggestChallenge === option ? 'is-on' : ''}`}
                              >
                                <span className="gm-opt-label">{option}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Task 2.3: Step 18 - Agent Partnership Importance (Conditional) */}
                    {currentStepId === 'agent-importance' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            How important are these agent partnerships to your business?
                          </h3>
                          <p className="gm-sub">
                            Help us understand your referral sources so we can optimize your strategy
                          </p>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              { value: 'Very important', label: 'Very important', subtitle: 'They\'re my primary referral source' },
                              { value: 'Important', label: 'Important', subtitle: 'They send me regular business' },
                              { value: 'Somewhat important', label: 'Somewhat important', subtitle: 'Occasional referrals' },
                              { value: 'Not important', label: 'Not important', subtitle: 'I get leads elsewhere' },
                            ].map((option) => (
                              <button
                                key={option.value}
                                onClick={() => handleAutoAdvance({ agentPartnershipImportance: option.value })}
                                data-dm-option className={`gm-opt ${formData.agentPartnershipImportance === option.value ? 'is-on' : ''}`}
                              >
                                <p className="font-semibold text-gray-900">{option.label}</p>
                                <p className="text-sm text-gray-600 mt-1">{option.subtitle}</p>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Lead Gen Approach Step */}
                    {currentStepId === 'lead-gen-approach' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            Which approach interests you more?
                          </h3>
                          <p className="gm-sub">
                            This helps us customize your strategy
                          </p>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              { value: 'build', label: 'Build my own lead generation system' },
                              { value: 'buy', label: 'Buy leads from a vendor' },
                              { value: 'both', label: 'I\'m open to both approaches' },
                              { value: 'unsure', label: 'Not sure yet - I need guidance' },
                            ].map((option) => (
                              <button
                                key={option.value}
                                onClick={() => handleAutoAdvance({
                                  leadGenApproach: option.value,
                                  systemPreference: option.label
                                })}
                                data-dm-option className={`gm-opt ${formData.leadGenApproach === option.value ? 'is-on' : ''}`}
                              >
                                <span className="gm-opt-label">{option.label}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 13: Timeline */}
                    {currentStepId === 'timeline' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            When do you want to have a new lead system running?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              'ASAP (this month)',
                              'Next 30 days',
                              'Next 60-90 days',
                              'Just researching for future',
                            ].map((option) => (
                              <button
                                key={option}
                                onClick={() => handleAutoAdvance({ timeline: option })}
                                data-dm-option className={`gm-opt ${formData.timeline === option ? 'is-on' : ''}`}
                              >
                                <span className="gm-opt-label">{option}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 14: Licensed States */}
                    {currentStepId === 'states' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            Which state(s) are you licensed in?
                          </h3>
                          <p className="gm-sub">
                            Select your primary state, then any additional states
                          </p>
                        </div>
                        <div className="gm-body">
                          <div className="space-y-6">
                            {/* Primary State Dropdown */}
                            <div>
                              <label className="gm-label">
                                Primary State *
                              </label>
                              <Dropdown
                                value={formData.primaryState}
                                onChange={(e) => setFormData(prev => ({ ...prev, primaryState: e.value }))}
                                options={allUSStates}
                                filter
                                placeholder="Select your primary state"
                                className="w-full"
                                panelClassName="prime-dropdown-panel"
                                style={{
                                  width: '100%',
                                  borderRadius: '0.5rem',
                                  border: '1px solid #d1d5db',
                                }}
                              />
                              {/* Note: EnrichedData doesn't include address, so we can't auto-select primaryState */}
                            </div>

                            {/* Additional States Multi-Select */}
                            <div>
                              <label className="gm-label">
                                Additional Important States (Optional)
                              </label>
                              <MultiSelect
                                value={formData.additionalStates}
                                onChange={(e) => setFormData(prev => ({ ...prev, additionalStates: e.value }))}
                                options={allUSStates.filter(state => state !== formData.primaryState)}
                                filter
                                placeholder="Select additional states"
                                maxSelectedLabels={3}
                                selectedItemsLabel="{0} more states"
                                filterDelay={400}
                                className="w-full"
                                panelClassName="prime-multiselect-panel"
                                style={{
                                  width: '100%',
                                  borderRadius: '0.5rem',
                                  border: '1px solid #d1d5db',
                                }}
                                display="chip"
                              />
                            </div>
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Loader A: Preparing Contact (3-second loader with sequenced messages) */}
                    {currentStepId === 'preparing-contact' && (
                      <ElegantLoader message={loaderAMessage} />
                    )}

                    {/* Non-Licensed Path Questions */}
                    {currentStepId === 'non-licensed-identity' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            Who are you?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              'Marketing Agency / Vendor',
                              'Non-QM or Commercial Loans Professional',
                              'Mortgage Operations Leader',
                              'Mortgage Executive (C-Suite/VP)',
                              'Real Estate Professional',
                              'Financial Services Professional',
                              'Tech/Software Provider',
                              'Other',
                            ].map((option) => (
                              <button
                                key={option}
                                onClick={() => {
                                  setNonLicensedIdentity(option);
                                  handleAutoAdvance({});
                                }}
                                data-dm-option className={`gm-opt ${nonLicensedIdentity === option ? 'is-on' : ''}`}
                              >
                                <span className="gm-opt-label">{option}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {currentStepId === 'non-licensed-intent' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            What are you looking for?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              'Partnership opportunities (referrals/white-label)',
                              'Marketing services for my mortgage team',
                              'Lead generation technology/platform',
                              'Training/consulting for mortgage marketing',
                              'Just researching / learning',
                              'Something else',
                            ].map((option) => (
                              <button
                                key={option}
                                onClick={() => {
                                  setNonLicensedIntent(option);
                                  handleAutoAdvance({});
                                }}
                                data-dm-option className={`gm-opt ${nonLicensedIntent === option ? 'is-on' : ''}`}
                              >
                                <span className="gm-opt-label">{option}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {currentStepId === 'non-licensed-timeline' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            How soon do you need a solution?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              'Immediately',
                              'Within 30 days',
                              'Next 2-3 months',
                              'Just exploring options',
                            ].map((option) => (
                              <button
                                key={option}
                                onClick={() => {
                                  setNonLicensedTimeline(option);
                                  handleAutoAdvance({});
                                }}
                                data-dm-option className={`gm-opt ${nonLicensedTimeline === option ? 'is-on' : ''}`}
                              >
                                <span className="gm-opt-label">{option}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {currentStepId === 'non-licensed-challenge' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            What's your biggest challenge?
                          </h3>
                        </div>
                        <div className="gm-body">
                          <div className="gm-list">
                            {[
                              'Need more mortgage clients/leads',
                              'Team needs better marketing systems',
                              'Want to add mortgage marketing services',
                              'Need to scale existing operations',
                              'Looking for partnership revenue',
                              'Other',
                            ].map((option) => (
                              <button
                                key={option}
                                onClick={() => {
                                  setNonLicensedChallenge(option);
                                  handleAutoAdvance({});
                                }}
                                data-dm-option className={`gm-opt ${nonLicensedChallenge === option ? 'is-on' : ''}`}
                              >
                                <span className="gm-opt-label">{option}</span>
                              </button>
                            ))}
                          </div>
                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Step 15: Contact Information */}
                    {currentStepId === 'contact' && (
                      <>
                        <div className="gm-head">
                          <h3 className="gm-q">
                            {source === 'pricing' ? "Perfect! Let's build your custom pricing" : "Perfect! Let's create your implementation plan"}
                          </h3>
                        </div>
                        <div className="gm-body">
                          <GMBoss valid={[fieldValidation.firstName.isValid, fieldValidation.lastName.isValid, fieldValidation.email.isValid, fieldValidation.phone.isValid].filter(Boolean).length} total={4} />
                          <div className="space-y-4">
                            {submitError && (
                              <div role="alert" className="gm-alert">
                                {submitError}
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                              {/* First Name */}
                              <div className="relative">
                                <label className="gm-label">
                                  First Name *
                                </label>
                                <div className="relative">
                                  <input
                                    type="text"
                                    name="firstName"
                                    aria-invalid={!fieldValidation.firstName.isValid && fieldValidation.firstName.isTouched}
                                    aria-describedby={!fieldValidation.firstName.isValid && fieldValidation.firstName.isTouched ? 'contact-firstName-error' : undefined}
                                    autoComplete="given-name"
                                    autoFocus
                                    value={formData.firstName}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setFormData(prev => ({ ...prev, firstName: value }));
                                      const isValid = validateFirstName(value);
                                      setFieldValidation(prev => ({
                                        ...prev,
                                        firstName: { ...prev.firstName, isValid }
                                      }));
                                    }}
                                    onAnimationStart={(e) => {
                                      if (e.animationName === 'onAutoFillStart') {
                                        const value = e.currentTarget.value;
                                        if (value !== formData.firstName) {
                                          setFormData(prev => ({ ...prev, firstName: value }));
                                          const isValid = validateFirstName(value);
                                          setFieldValidation(prev => ({
                                            ...prev,
                                            firstName: { ...prev.firstName, isValid }
                                          }));
                                        }
                                      }
                                    }}
                                    onFocus={(e) => {
                                      const value = e.currentTarget.value;
                                      if (value && value !== formData.firstName) {
                                        setFormData(prev => ({ ...prev, firstName: value }));
                                        const isValid = validateFirstName(value);
                                        setFieldValidation(prev => ({
                                          ...prev,
                                          firstName: { ...prev.firstName, isValid }
                                        }));
                                      }
                                    }}
                                    onBlur={() => {
                                      setFieldValidation(prev => ({
                                        ...prev,
                                        firstName: { ...prev.firstName, isTouched: true }
                                      }));
                                    }}
                                    className={`gm-input gm-pr ${fieldValidation.firstName.isValid ? 'is-valid' : fieldValidation.firstName.isTouched ? 'is-invalid' : ''}`}
                                    placeholder="John"
                                  />
                                  {fieldValidation.firstName.isValid && (
                                    <motion.div
                                      initial={{ opacity: 0, scale: 0.5, rotate: -90 }}
                                      animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                      transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
                                      className="absolute right-3 top-3 bottom-3 flex items-center"
                                      style={{ pointerEvents: 'none' }}
                                    >
                                      <Check className="w-5 h-5 text-green-500" strokeWidth={3} />
                                    </motion.div>
                                  )}
                                  {!fieldValidation.firstName.isValid && fieldValidation.firstName.isTouched && (
                                    <motion.div
                                      initial={{ opacity: 0, x: 0 }}
                                      animate={{ opacity: 1, x: [0, -4, 4, -4, 4, 0] }}
                                      transition={{ duration: 0.4 }}
                                      className="absolute right-3 top-3 bottom-3 flex items-center"
                                      style={{ pointerEvents: 'none' }}
                                    >
                                      <X className="w-5 h-5 text-red-500" strokeWidth={3} />
                                    </motion.div>
                                  )}
                                </div>
                                {!fieldValidation.firstName.isValid && fieldValidation.firstName.isTouched && (
                                  <p id="contact-firstName-error" role="alert" className="gm-err">
                                    Enter your first name, at least 2 letters.
                                  </p>
                                )}
                                {/* B8: Verified indicator for auto-filled firstName */}
                                {verifiedFields && formData.firstName && formData.firstName.length > 0 && (
                                  <motion.p
                                    initial={{ opacity: 0, y: -5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="text-xs text-blue-600 font-medium mt-1 flex items-center gap-1"
                                  >
                                    <Check className="w-3 h-3" />
                                    Auto-filled from verified profile
                                  </motion.p>
                                )}
                              </div>

                              {/* Last Name */}
                              <div className="relative">
                                <label className="gm-label">
                                  Last Name *
                                </label>
                                <div className="relative">
                                  <input
                                    type="text"
                                    name="lastName"
                                    aria-invalid={!fieldValidation.lastName.isValid && fieldValidation.lastName.isTouched}
                                    aria-describedby={!fieldValidation.lastName.isValid && fieldValidation.lastName.isTouched ? 'contact-lastName-error' : undefined}
                                    autoComplete="family-name"
                                    value={formData.lastName}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setFormData(prev => ({ ...prev, lastName: value }));
                                      const isValid = validateLastName(value);
                                      setFieldValidation(prev => ({
                                        ...prev,
                                        lastName: { ...prev.lastName, isValid }
                                      }));
                                    }}
                                    onAnimationStart={(e) => {
                                      if (e.animationName === 'onAutoFillStart') {
                                        const value = e.currentTarget.value;
                                        if (value !== formData.lastName) {
                                          setFormData(prev => ({ ...prev, lastName: value }));
                                          const isValid = validateLastName(value);
                                          setFieldValidation(prev => ({
                                            ...prev,
                                            lastName: { ...prev.lastName, isValid }
                                          }));
                                        }
                                      }
                                    }}
                                    onFocus={(e) => {
                                      const value = e.currentTarget.value;
                                      if (value && value !== formData.lastName) {
                                        setFormData(prev => ({ ...prev, lastName: value }));
                                        const isValid = validateLastName(value);
                                        setFieldValidation(prev => ({
                                          ...prev,
                                          lastName: { ...prev.lastName, isValid }
                                        }));
                                      }
                                    }}
                                    onBlur={() => {
                                      setFieldValidation(prev => ({
                                        ...prev,
                                        lastName: { ...prev.lastName, isTouched: true }
                                      }));
                                    }}
                                    className={`gm-input gm-pr ${fieldValidation.lastName.isValid ? 'is-valid' : fieldValidation.lastName.isTouched ? 'is-invalid' : ''}`}
                                    placeholder="Smith"
                                  />
                                  {fieldValidation.lastName.isValid && (
                                    <motion.div
                                      initial={{ opacity: 0, scale: 0.5, rotate: -90 }}
                                      animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                      transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
                                      className="absolute right-3 top-3 bottom-3 flex items-center"
                                      style={{ pointerEvents: 'none' }}
                                    >
                                      <Check className="w-5 h-5 text-green-500" strokeWidth={3} />
                                    </motion.div>
                                  )}
                                  {!fieldValidation.lastName.isValid && fieldValidation.lastName.isTouched && (
                                    <motion.div
                                      initial={{ opacity: 0, x: 0 }}
                                      animate={{ opacity: 1, x: [0, -4, 4, -4, 4, 0] }}
                                      transition={{ duration: 0.4 }}
                                      className="absolute right-3 top-3 bottom-3 flex items-center"
                                      style={{ pointerEvents: 'none' }}
                                    >
                                      <X className="w-5 h-5 text-red-500" strokeWidth={3} />
                                    </motion.div>
                                  )}
                                </div>
                                {!fieldValidation.lastName.isValid && fieldValidation.lastName.isTouched && (
                                  <p id="contact-lastName-error" role="alert" className="gm-err">
                                    Enter your last name, at least 2 letters.
                                  </p>
                                )}
                                {/* B8: Verified indicator for auto-filled lastName */}
                                {verifiedFields && formData.lastName && formData.lastName.length > 0 && (
                                  <motion.p
                                    initial={{ opacity: 0, y: -5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="text-xs text-blue-600 font-medium mt-1 flex items-center gap-1"
                                  >
                                    <Check className="w-3 h-3" />
                                    Auto-filled from verified profile
                                  </motion.p>
                                )}
                              </div>
                            </div>

                            {/* Email */}
                            <div className="relative">
                              <label className="gm-label">
                                Email *
                              </label>
                              <div className="relative">
                                <input
                                  type="email"
                                  name="email"
                                  aria-invalid={!fieldValidation.email.isValid && fieldValidation.email.isTouched}
                                  aria-describedby={!fieldValidation.email.isValid && fieldValidation.email.isTouched ? 'contact-email-error' : undefined}
                                  autoComplete="email"
                                  value={formData.email}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setFormData(prev => ({ ...prev, email: value }));
                                    const isValid = validateEmail(value);
                                    setFieldValidation(prev => ({
                                      ...prev,
                                      email: { ...prev.email, isValid }
                                    }));
                                  }}
                                  onAnimationStart={(e) => {
                                    if (e.animationName === 'onAutoFillStart') {
                                      const value = e.currentTarget.value;
                                      if (value !== formData.email) {
                                        setFormData(prev => ({ ...prev, email: value }));
                                        const isValid = validateEmail(value);
                                        setFieldValidation(prev => ({
                                          ...prev,
                                          email: { ...prev.email, isValid }
                                        }));
                                      }
                                    }
                                  }}
                                  onFocus={(e) => {
                                    const value = e.currentTarget.value;
                                    if (value && value !== formData.email) {
                                      setFormData(prev => ({ ...prev, email: value }));
                                      const isValid = validateEmail(value);
                                      setFieldValidation(prev => ({
                                        ...prev,
                                        email: { ...prev.email, isValid }
                                      }));
                                    }
                                  }}
                                  onBlur={() => {
                                    setFieldValidation(prev => ({
                                      ...prev,
                                      email: { ...prev.email, isTouched: true }
                                    }));
                                  }}
                                  className={`gm-input gm-pr ${fieldValidation.email.isValid ? 'is-valid' : fieldValidation.email.isTouched ? 'is-invalid' : ''}`}
                                  placeholder="john@example.com"
                                />
                                {fieldValidation.email.isValid && (
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.5, rotate: -90 }}
                                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                    transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
                                    className="absolute right-3 top-3 bottom-3 flex items-center"
                                    style={{ pointerEvents: 'none' }}
                                  >
                                    <Check className="w-5 h-5 text-green-500" strokeWidth={3} />
                                  </motion.div>
                                )}
                                {!fieldValidation.email.isValid && fieldValidation.email.isTouched && (
                                  <motion.div
                                    initial={{ opacity: 0, x: 0 }}
                                    animate={{ opacity: 1, x: [0, -4, 4, -4, 4, 0] }}
                                    transition={{ duration: 0.4 }}
                                    className="absolute right-3 top-3 bottom-3 flex items-center"
                                    style={{ pointerEvents: 'none' }}
                                  >
                                    <X className="w-5 h-5 text-red-500" strokeWidth={3} />
                                  </motion.div>
                                )}
                              </div>
                              {!fieldValidation.email.isValid && fieldValidation.email.isTouched && (
                                <p id="contact-email-error" role="alert" className="gm-err">
                                  Enter a valid email, like name@company.com.
                                </p>
                              )}
                            </div>

                            {/* Phone */}
                            <div className="relative">
                              <label className="gm-label">
                                Phone *
                              </label>
                              <div className="relative">
                                <input
                                  type="tel"
                                  name="phone"
                                  aria-invalid={!fieldValidation.phone.isValid && fieldValidation.phone.isTouched}
                                  aria-describedby={!fieldValidation.phone.isValid && fieldValidation.phone.isTouched ? 'contact-phone-error' : undefined}
                                  autoComplete="tel"
                                  inputMode="numeric"
                                  value={formData.phone}
                                  onChange={(e) => {
                                    const formatted = formatPhoneNumber(e.target.value);
                                    setFormData(prev => ({ ...prev, phone: formatted }));
                                    const isValid = validatePhone(formatted);
                                    setFieldValidation(prev => ({
                                      ...prev,
                                      phone: { ...prev.phone, isValid }
                                    }));
                                  }}
                                  onAnimationStart={(e) => {
                                    if (e.animationName === 'onAutoFillStart') {
                                      const formatted = formatPhoneNumber(e.currentTarget.value);
                                      if (formatted !== formData.phone) {
                                        setFormData(prev => ({ ...prev, phone: formatted }));
                                        const isValid = validatePhone(formatted);
                                        setFieldValidation(prev => ({
                                          ...prev,
                                          phone: { ...prev.phone, isValid }
                                        }));
                                      }
                                    }
                                  }}
                                  onFocus={(e) => {
                                    const formatted = formatPhoneNumber(e.currentTarget.value);
                                    if (formatted && formatted !== formData.phone) {
                                      setFormData(prev => ({ ...prev, phone: formatted }));
                                      const isValid = validatePhone(formatted);
                                      setFieldValidation(prev => ({
                                        ...prev,
                                        phone: { ...prev.phone, isValid }
                                      }));
                                    }
                                  }}
                                  onBlur={() => {
                                    setFieldValidation(prev => ({
                                      ...prev,
                                      phone: { ...prev.phone, isTouched: true }
                                    }));
                                  }}
                                  className={`gm-input gm-pr ${fieldValidation.phone.isValid ? 'is-valid' : fieldValidation.phone.isTouched ? 'is-invalid' : ''}`}
                                  placeholder="(555) 123-4567"
                                />
                                {fieldValidation.phone.isValid && (
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.5, rotate: -90 }}
                                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                    transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
                                    className="absolute right-3 top-3 bottom-3 flex items-center"
                                    style={{ pointerEvents: 'none' }}
                                  >
                                    <Check className="w-5 h-5 text-green-500" strokeWidth={3} />
                                  </motion.div>
                                )}
                                {!fieldValidation.phone.isValid && fieldValidation.phone.isTouched && (
                                  <motion.div
                                    initial={{ opacity: 0, x: 0 }}
                                    animate={{ opacity: 1, x: [0, -4, 4, -4, 4, 0] }}
                                    transition={{ duration: 0.4 }}
                                    className="absolute right-3 top-3 bottom-3 flex items-center"
                                    style={{ pointerEvents: 'none' }}
                                  >
                                    <X className="w-5 h-5 text-red-500" strokeWidth={3} />
                                  </motion.div>
                                )}
                              </div>
                              {!fieldValidation.phone.isValid && fieldValidation.phone.isTouched && (
                                <p id="contact-phone-error" role="alert" className="gm-err">
                                  Enter a valid 10-digit US phone number.
                                </p>
                              )}
                            </div>

                            {/* Company Name */}
                            <div className="relative">
                              <label className="gm-label">
                                Company/Team Name *
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  name="companyName"
                                  aria-invalid={!fieldValidation.companyName.isValid && fieldValidation.companyName.isTouched}
                                  aria-describedby={!fieldValidation.companyName.isValid && fieldValidation.companyName.isTouched ? 'contact-companyName-error' : undefined}
                                  ref={companyInputRef}
                                  autoComplete="organization"
                                  value={formData.companyName}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setFormData(prev => ({ ...prev, companyName: value }));
                                    setCompanyFromProfile(false);
                                    const isValid = validateCompanyName(value);
                                    setFieldValidation(prev => ({
                                      ...prev,
                                      companyName: { ...prev.companyName, isValid }
                                    }));
                                  }}
                                  onAnimationStart={(e) => {
                                    if (e.animationName === 'onAutoFillStart') {
                                      const value = e.currentTarget.value;
                                      if (value !== formData.companyName) {
                                        setFormData(prev => ({ ...prev, companyName: value }));
                                        setCompanyFromProfile(false);
                                        const isValid = validateCompanyName(value);
                                        setFieldValidation(prev => ({
                                          ...prev,
                                          companyName: { ...prev.companyName, isValid }
                                        }));
                                      }
                                    }
                                  }}
                                  onFocus={(e) => {
                                    const value = e.currentTarget.value;
                                    if (value && value !== formData.companyName) {
                                      setFormData(prev => ({ ...prev, companyName: value }));
                                      setCompanyFromProfile(false);
                                      const isValid = validateCompanyName(value);
                                      setFieldValidation(prev => ({
                                        ...prev,
                                        companyName: { ...prev.companyName, isValid }
                                      }));
                                    }
                                  }}
                                  onBlur={() => {
                                    setFieldValidation(prev => ({
                                      ...prev,
                                      companyName: { ...prev.companyName, isTouched: true }
                                    }));
                                  }}
                                  className={`gm-input gm-pr ${showChangeCompany ? 'gm-pr-btn' : ''} ${fieldValidation.companyName.isValid ? (formData.companyName.length > 0 ? 'is-valid' : '') : fieldValidation.companyName.isTouched ? 'is-invalid' : ''}`}
                                  placeholder="Your company"
                                />
                                {fieldValidation.companyName.isValid && formData.companyName.length > 0 && !showChangeCompany && (
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.5, rotate: -90 }}
                                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                    transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
                                    className="absolute right-3 top-3 bottom-3 flex items-center"
                                    style={{ pointerEvents: 'none' }}
                                  >
                                    <Check className="w-5 h-5 text-green-500" strokeWidth={3} />
                                  </motion.div>
                                )}
                                {showChangeCompany && (
                                  <button
                                    type="button"
                                    onClick={handleChangeCompany}
                                    aria-label="Change company, clear the name filled in from your profile"
                                    className="gm-field-btn"
                                  >
                                    Change company
                                  </button>
                                )}
                                {!fieldValidation.companyName.isValid && fieldValidation.companyName.isTouched && (
                                  <motion.div
                                    initial={{ opacity: 0, x: 0 }}
                                    animate={{ opacity: 1, x: [0, -4, 4, -4, 4, 0] }}
                                    transition={{ duration: 0.4 }}
                                    className="absolute right-3 top-3 bottom-3 flex items-center"
                                    style={{ pointerEvents: 'none' }}
                                  >
                                    <X className="w-5 h-5 text-red-500" strokeWidth={3} />
                                  </motion.div>
                                )}
                              </div>
                              {!fieldValidation.companyName.isValid && fieldValidation.companyName.isTouched && (
                                <p id="contact-companyName-error" role="alert" className="gm-err">
                                  Enter your company or team name, at least 2 characters.
                                </p>
                              )}
                              {/* B8: Verified indicator for auto-filled company */}
                              {showChangeCompany && (
                                <motion.p
                                  initial={{ opacity: 0, y: -5 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: 0.2 }}
                                  className="text-xs text-blue-600 font-medium mt-1 flex items-center gap-1"
                                >
                                  <Check className="w-3 h-3" />
                                  Auto-filled from verified profile
                                </motion.p>
                              )}
                            </div>

                            {/* Best Time to Call */}
                            <div>
                              <label className="gm-label">
                                Best time to call *
                              </label>
                              <Dropdown
                                value={formData.bestTimeToCall}
                                onChange={(e) => setFormData(prev => ({ ...prev, bestTimeToCall: e.value }))}
                                options={['Anytime', 'Morning', 'Afternoon', 'Evening']}
                                placeholder="Select best time to call"
                                className="w-full"
                                panelClassName="prime-dropdown-panel prime-dropdown-panel-no-scroll"
                                style={{
                                  width: '100%',
                                  borderRadius: '0.5rem',
                                  border: '1px solid #d1d5db',
                                }}
                              />
                            </div>
                          </div>

                          {/* TCPA Consent Disclaimer */}
                          <div className="mt-4 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-[11px] md:text-[10px] text-gray-600 leading-snug text-center">
                              By clicking "{finalSubmitText}", I agree that LeadPops (rebel iQ, Inc.) may email, call and text me, including by automated means, at the contact info I provided about its services. Consent is not a condition of purchase. Msg &amp; data rates may apply; reply STOP to opt out. See our <a href="/privacy-policy#notice-at-collection" target="_blank" rel="noopener" className="underline">Privacy Policy</a>.
                            </p>
                          </div>

                          <SecurityBadge />
                        </div>
                      </>
                    )}

                    {/* Loader B: Submitting (5-second loader with sequenced messages) */}
                    {currentStepId === 'submitting' && (
                      <ElegantLoader message={loaderBMessage} showSuccess={showSuccessAnimation} />
                    )}

                    {/* Step 16: Thank You */}
                    {currentStepId === 'thankyou' && (
                      <div className="gm-body">
                        <div className="space-y-4 max-w-3xl mx-auto">
                          {/* Simple BOOK NOW prompt */}
                          <div className="text-center pt-4">
                            <p className="text-lg font-semibold text-gray-900 flex items-center justify-center gap-2">
                              BOOK NOW
                              <motion.span
                                className="text-2xl"
                                animate={{ y: [0, 6, 0] }}
                                transition={{
                                  duration: 1.4,
                                  repeat: Infinity,
                                  ease: "easeInOut",
                                }}
                              >
                                ↓
                              </motion.span>
                            </p>
                          </div>

                          {/* HubSpot Calendar */}
                          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mx-auto" style={{ maxWidth: '450px' }}>
                            <HubSpotCalendar />
                          </div>

                          {/* What to Expect Section */}
                          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-100">
                            <h4 className="text-lg font-bold text-gray-900 mb-4">
                              What to Expect on Your Strategy Session
                            </h4>
                            <div className="space-y-3">
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold mt-0.5">
                                  1
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">Custom {formData.primaryState} Market Analysis</p>
                                  <p className="text-sm text-gray-600">We'll review competitive landscape and opportunity zones specific to your market</p>
                                </div>
                              </div>
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold mt-0.5">
                                  2
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">ROI Projections Based on Your Numbers</p>
                                  <p className="text-sm text-gray-600">See exactly how our system would perform with your specific budget and goals</p>
                                </div>
                              </div>
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold mt-0.5">
                                  3
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">90-Day Implementation Roadmap</p>
                                  <p className="text-sm text-gray-600">Step-by-step plan to get your lead generation system running</p>
                                </div>
                              </div>
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold mt-0.5">
                                  4
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">Honest Assessment</p>
                                  <p className="text-sm text-gray-600">We'll tell you if LeadPops is the right fit - no pressure, no pitch</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Trust Footer */}
                          <div className="text-center text-sm text-gray-600 pb-4">
                            <p className="font-medium">No courses. No coaching. No BS.</p>
                            <p className="mt-1">Just a real conversation about growing your business in {formData.primaryState}.</p>
                          </div>

                          {/* Social Proof Section */}
                          <div className="border-t border-gray-200 pt-6">
                            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
                              <div className="flex items-center gap-2">
                                <span className="text-lg text-yellow-500">★★★★★</span>
                                <span className="font-medium">4.9 from 750+ reviews</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-blue-600">
                                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                                </svg>
                                <span>5,247+ mortgage pros trust our system</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-green-600">
                                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                </svg>
                                <span>3.2M+ leads generated by our users</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Footer Navigation */}
              {currentStepId !== 'thankyou' && currentStepId !== 'welcome' && currentStepId !== 'preparing-contact' && currentStepId !== 'submitting' && (
                <div className="gm-foot">
                  <div className="flex items-center gap-2">
                    {currentStep > 0 && currentStepId !== 'welcome' && currentStepId !== 'nmls-confirm' ? (
                      <button
                        onClick={handleBack}
                        className="gm-btn gm-btn-quiet"
                      >
                        Back
                      </button>
                    ) : (
                      <div />
                    )}
                  </div>

                  {/* Show Next/Submit button for multi-select, text-input, and contact steps */}
                  {(currentStepId === 'sources' || currentStepId === 'website-url' || currentStepId === 'states' || currentStepId === 'contact') && (
                    <button
                      onClick={async () => {
                        console.log('🔘🔘🔘 BUTTON CLICKED - Step:', currentStepId, 'isValid:', isStepValid());
                        if (currentStepId === 'contact') {
                          setIsSubmitting(true);
                          console.log('🔘 Calling handleSubmit()...');
                          await handleSubmit();
                          console.log('🔘 handleSubmit() completed');
                        } else {
                          handleNext();
                        }
                      }}
                      disabled={!isStepValid() || isSubmitting}
                      className="gm-btn gm-btn-primary"
                    >
                      {isSubmitting ? (
                        <>
                          <motion.div
                            className="h-4 w-4 border-2 border-white border-t-transparent rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                          />
                          Processing...
                        </>
                      ) : (
                        <>
                          {currentStepId === 'contact' ? finalSubmitText : 'Continue'}
                          <ChevronRight className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  )}

                  {/* Verification Buttons */}
                  {currentStepId === 'nmls-confirm' && enrichedData && !showVerificationSuccess && (
                    <div className="flex gap-4 w-full">
                      <button
                        type="button"
                        onClick={handleRejectNMLS}
                        className="gm-btn gm-btn-ghost flex-1"
                      >
                        No, that's not me
                      </button>
                      <button
                        type="button"
                        onClick={handleConfirmNMLS}
                        disabled={confirmingNMLS}
                        className="gm-btn gm-btn-primary flex-1"
                      >
                        {confirmingNMLS ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Confirming...
                          </>
                        ) : (
                          "Yes, that's me"
                        )}
                      </button>
                    </div>
                  )}

                  {/* Timeout Button */}
                  {currentStepId === 'nmls-confirm' && matchStatus === 'timeout' && !enrichedData && (
                    <div className="flex w-full justify-end">
                      <button
                        type="button"
                        onClick={() => handleNext()}
                        className="gm-btn gm-btn-primary"
                      >
                        Continue
                      </button>
                    </div>
                  )}

                  {/* No Match Buttons */}
                  {currentStepId === 'nmls-confirm' && matchStatus === 'not_found' && (
                    <div className="flex gap-4 w-full">
                      <button
                        type="button"
                        onClick={() => {
                          console.log('User clicked: I don\'t remember (from no-match screen)');

                          // Clear NMLS data and contact fields
                          setFormData(prev => ({
                            ...prev,
                            nmls: '',
                            firstName: '',
                            lastName: '',
                            companyName: '',
                            email: '',
                            phone: ''
                          }));
                          setEnrichedData(null);
                          setEnriched(false);
                          setMatchStatus(null);

                          // Skip verification
                          setSkipVerification(true);

                          // Trigger navigation via useEffect (avoids stale closure)
                          setShouldNavigateToContact(true);
                        }}
                        className="gm-btn gm-btn-ghost flex-1"
                      >
                        I don't remember my NMLS number
                      </button>
                      <button
                        type="button"
                        onClick={handleEditNMLS}
                        className="gm-btn gm-btn-primary flex-1"
                      >
                        Edit NMLS #
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* PERSISTENT DEBUG BAR - HIDDEN (kept for future debugging) */}
              {false && debugResponse && (
                <div style={{position: 'fixed', bottom: 0, left: 0, right: 0, background: 'black', color: 'lime', padding: '15px', fontSize: '12px', fontFamily: 'monospace', zIndex: 999999, lineHeight: '1.6'}}>
                  <div><strong>LAST API RESPONSE [{debugResponse.timestamp}]:</strong></div>
                  <div>success={debugResponse.success ? 'TRUE' : 'FALSE'} | found={debugResponse.found ? 'TRUE' : 'FALSE'} | hasData={debugResponse.hasData ? 'TRUE' : 'FALSE'}</div>
                  <div>dataKeys: {debugResponse.dataKeys}</div>
                  <div>matchStatus: {matchStatus || 'null'} | enriched: {enriched ? 'true' : 'false'} | enrichedData: {enrichedData ? 'EXISTS' : 'NULL'}</div>
                </div>
              )}
            </motion.div>
    </GMGame>
  );
}
